<template>
    <div class="request-type-editor requests-glass argus-glass-page">
        <!-- Заголовок с навигацией -->
        <div class="request-type-editor-header">
            <div class="request-type-editor-header__left">
                <b-button variant="outline-secondary" size="sm" @click="handleCancel" class="mr-2">
                    <i class="fas fa-arrow-left"></i> Назад
                </b-button>
                <div class="request-type-editor-title">
                    <div class="request-type-editor-title__mode">
                        {{ editMode ? 'Редактирование типа заявки' : 'Создание типа заявки' }}
                    </div>
                    <h4 class="request-type-editor-title__name">
                        {{ form.name && form.name.trim() ? form.name : 'Новый тип заявки' }}
                    </h4>
                    <div class="request-type-editor-title__meta">
                        {{ getCategoryLabel(form.category) || 'Категория не выбрана' }}
                        <span class="mx-1">/</span>
                        {{ activeRequestTypeSection.title }}
                    </div>
                </div>
            </div>
            <div class="request-type-editor-header__actions">
                <b-button variant="outline-secondary" size="sm" @click="handleCancel" class="mr-2">
                    Отмена
                </b-button>
                <b-button variant="primary" size="sm" @click="handleSubmit" :disabled="saving">
                    <b-spinner v-if="saving" small class="mr-1"></b-spinner>
                    <i v-else class="fas fa-save"></i>
                    Сохранить
                </b-button>
            </div>
        </div>

        <b-form @submit.prevent="handleSubmit">
            <div class="request-type-shell">
                <aside class="request-type-sidebar" aria-label="Разделы настройки типа заявки">
                    <div class="request-type-sidebar__caption">Настройки типа</div>
                    <div class="request-type-parameter-search">
                        <b-input-group size="sm">
                            <template #prepend>
                                <b-input-group-text>
                                    <i class="fas fa-search"></i>
                                </b-input-group-text>
                            </template>
                            <b-form-input
                                v-model="settingsSearchQuery"
                                aria-label="Поиск по параметрам типа заявки"
                                placeholder="Найти параметр"
                            />
                            <template #append>
                                <b-button
                                    v-if="settingsSearchQuery"
                                    variant="outline-secondary"
                                    aria-label="Очистить поиск"
                                    @click="clearSettingsSearch"
                                >
                                    <i class="fas fa-times"></i>
                                </b-button>
                            </template>
                        </b-input-group>
                        <div
                            v-if="normalizedSettingsSearchQuery"
                            class="request-type-parameter-search__meta"
                        >
                            {{ searchResultsLabel }}
                        </div>
                    </div>
                    <template v-for="section in searchableRequestTypeSections">
                        <button
                            :key="`${section.key}-nav`"
                            type="button"
                            class="request-type-nav-button"
                            :class="{ 'request-type-nav-button--active': activeRequestTypeSection.key === section.key }"
                            @click="setActiveSection(section.key)"
                        >
                            <span class="request-type-nav-button__icon">
                                <i :class="section.icon"></i>
                            </span>
                            <span class="request-type-nav-button__body">
                                <span class="request-type-nav-button__title">{{ section.title }}</span>
                                <span class="request-type-nav-button__summary">{{ section.searchSummary || section.summary }}</span>
                            </span>
                            <span
                                v-if="section.issueCount"
                                class="request-type-nav-button__badges"
                            >
                                <b-badge
                                    variant="warning"
                                    class="request-type-nav-button__badge"
                                >
                                    {{ section.issueCount }}
                                </b-badge>
                            </span>
                        </button>
                        <div
                            v-if="normalizedSettingsSearchQuery && section.searchResults.length"
                            :key="`${section.key}-search-results`"
                            class="request-type-search-results"
                        >
                            <button
                                v-for="result in section.searchResults"
                                :key="result.key"
                                type="button"
                                class="request-type-search-result"
                                :class="{ 'request-type-search-result--active': highlightedSearchTargetKey === result.targetKey }"
                                @click="openSearchResult(section.key, result)"
                            >
                                <i class="fas fa-arrow-right"></i>
                                <span>{{ result.label }}</span>
                            </button>
                        </div>
                    </template>
                    <div
                        v-if="normalizedSettingsSearchQuery && searchableRequestTypeSections.length === 0"
                        class="request-type-parameter-search__empty"
                    >
                        Ничего не найдено
                    </div>
                </aside>

                <main class="request-type-main">
                    <div class="request-type-main__header">
                        <div>
                            <h5 class="mb-1">
                                <i :class="activeRequestTypeSection.icon"></i>
                                {{ activeRequestTypeSection.title }}
                            </h5>
                            <div class="text-muted small">{{ activeRequestTypeSection.description }}</div>
                        </div>
                        <b-badge :variant="form.is_active ? 'success' : 'secondary'">
                            {{ form.is_active ? 'Активен' : 'Отключен' }}
                        </b-badge>
                    </div>

                    <div class="request-type-summary-grid">
                        <div
                            v-for="item in requestTypeSummary"
                            :key="item.key"
                            class="request-type-summary-item"
                        >
                            <div class="request-type-summary-item__label">
                                <i :class="item.icon"></i>
                                {{ item.label }}
                            </div>
                            <div class="request-type-summary-item__value">{{ item.value }}</div>
                        </div>
                    </div>

                    <b-alert
                        v-if="configurationIssues.length"
                        show
                        variant="warning"
                        class="request-type-issues"
                    >
                        <div class="request-type-issues__title">
                            <i class="fas fa-exclamation-triangle"></i>
                            Стоит проверить перед сохранением
                        </div>
                        <div
                            v-for="issue in configurationIssues"
                            :key="issue.key"
                            class="request-type-issues__item"
                        >
                            {{ issue.text }}
                        </div>
                    </b-alert>

                    <b-tabs
                        v-model="activeSectionIndex"
                        nav-class="d-none"
                        content-class="request-type-tabs-content"
                        no-fade
                    >
                    <!-- Вкладка "Основные" -->
                    <b-tab v-if="isSectionAvailable('passport')" key="passport">
                        <template #title>
                            <i class="fas fa-id-card"></i>
                            <span class="d-none d-md-inline"> Паспорт</span>
                        </template>
                        
                        <!-- Основная информация -->
                        <div class="mb-4" data-search-target="passport-basics">
                            <h6 class="mb-3 text-primary">
                                <i class="fas fa-info-circle"></i> Основная информация
                            </h6>
                            <b-row>
                                <b-col cols="12" md="6">
                                    <b-form-group label="Название типа заявки *" label-for="name">
                                        <b-form-input
                                            id="name"
                                            v-model="form.name"
                                            placeholder="Например: Заявка на закупку материалов"
                                            required
                                        />
                                    </b-form-group>
                                </b-col>
                                <b-col cols="12" md="6">
                                    <b-form-group label="Категория *" label-for="category">
                                        <v-select
                                            id="category"
                                            v-model="form.category"
                                            :options="categoryOptions"
                                            label="text"
                                            :reduce="option => option.value"
                                            placeholder="Выберите категорию"
                                            :clearable="false"
                                            @input="onCategoryChange"
                                        />
                                    </b-form-group>
                                </b-col>
                            </b-row>

                            <b-form-group label="Описание" label-for="description">
                                <AppTextarea
                                    id="description"
                                    v-model="form.description"
                                    placeholder="Краткое описание типа заявки"
                                />
                            </b-form-group>

                            <b-form-checkbox v-model="form.is_active" switch>
                                Активен
                            </b-form-checkbox>

                            <b-form-checkbox v-if="!isSubmissionExtension" v-model="form.requires_title" switch class="mt-3">
                                Требовать поле "Название заявки"
                            </b-form-checkbox>
                            <small v-if="!isSubmissionExtension" class="text-muted d-block mt-1">
                                Если выключено, поле "Название заявки" не будет отображаться в форме создания
                            </small>

                            <div class="border rounded p-3 mt-3 bg-light" data-search-target="passport-appearance">
                                <h6 class="mb-2">Внешний вид заявки</h6>
                                <b-form-group label="Иконка блока при создании" label-for="request-type-icon">
                                    <div id="request-type-icon" class="request-type-icon-grid">
                                        <button
                                            v-for="iconOption in requestTypeIconOptions"
                                            :key="iconOption.value"
                                            type="button"
                                            class="request-type-icon-button"
                                            :class="{ 'request-type-icon-button--active': form.workflow_config.requestTypeIcon === iconOption.value }"
                                            :title="iconOption.text"
                                            :aria-label="`Выбрать иконку: ${iconOption.text}`"
                                            @click="setRequestTypeIcon(iconOption.value)"
                                        >
                                            <i :class="iconOption.value"></i>
                                        </button>
                                    </div>
                                </b-form-group>
                                <b-form-group label="Цвет линии карточки" label-for="card-border-variant" class="mb-0">
                                    <div id="card-border-variant" class="d-flex flex-wrap">
                                        <button
                                            v-for="variant in cardBorderVariants"
                                            :key="variant"
                                            type="button"
                                            class="request-type-color-swatch mr-2 mb-2"
                                            :class="[
                                                `bg-${variant}`,
                                                { 'request-type-color-swatch--active': form.workflow_config.cardBorderVariant === variant }
                                            ]"
                                            :aria-label="`Выбрать ${variant}`"
                                            @click="setCardBorderVariant(variant)"
                                        />
                                    </div>
                                </b-form-group>
                            </div>
                        </div>
                    </b-tab>

                    <b-tab v-if="isSectionAvailable('form')" key="form">
                        <template #title>
                            <i class="fas fa-clipboard-list"></i>
                            <span class="d-none d-md-inline"> Форма</span>
                        </template>

                        <div class="mb-4">
                            <div v-if="!isSubmissionExtension" class="border rounded p-3 mt-3 bg-light" data-search-target="form-description">
                                <h6 class="mb-2">Поле описания в заявке</h6>
                                <b-form-checkbox v-model="form.workflow_config.descriptionField.enabled" switch>
                                    Показывать поле "Описание" при создании заявки
                                </b-form-checkbox>
                                <b-form-checkbox
                                    v-if="form.workflow_config.descriptionField.enabled"
                                    v-model="form.workflow_config.descriptionField.required"
                                    switch
                                    class="mt-2"
                                >
                                    Требовать заполнение описания
                                </b-form-checkbox>
                            </div>

                            <div v-if="!isSubmissionExtension && !isReportAnnulment" class="border rounded p-3 mt-3 bg-light" data-search-target="form-building-process">
                                <h6 class="mb-2">Строительный процесс</h6>
                                <b-form-checkbox
                                    v-model="form.workflow_config.buildingProcess.enabled"
                                    switch
                                    @change="onBuildingProcessEnabledChange"
                                >
                                    Показывать поле "Строительный процесс" при создании заявки
                                </b-form-checkbox>
                                <b-form-checkbox
                                    v-if="form.workflow_config.buildingProcess.enabled"
                                    v-model="form.workflow_config.buildingProcess.required"
                                    switch
                                    class="mt-2"
                                    @change="onBuildingProcessRequiredChange"
                                >
                                    Обязательно для заполнения
                                </b-form-checkbox>
                                <small class="text-muted d-block mt-1">
                                    Если обязательность выключена, поле остается рекомендательным: пользователь может выбрать объект,
                                    но создание заявки не блокируется.
                                </small>
                            </div>

                            <div v-if="!isSubmissionExtension && !isReportAnnulment" class="border rounded p-3 mt-3 bg-light" data-search-target="form-editing">
                                <h6 class="mb-2">
                                    <i class="fas fa-edit"></i> Редактирование заявки
                                </h6>
                                <small class="text-muted d-block mb-3">
                                    По умолчанию инициатор и согласующие могут редактировать заявку до первого согласования.
                                    Если согласующих нет, редактирование доступно до взятия заявки в работу.
                                </small>

                                <b-form-checkbox
                                    v-model="form.workflow_config.requestEditing.allowAfterApproval"
                                    switch
                                    class="mb-3"
                                >
                                    Разрешить редактирование после начала согласования
                                    <small class="text-muted d-block">
                                        Заявку и список позиций можно будет менять даже после первого согласования.
                                    </small>
                                </b-form-checkbox>

                                <b-form-checkbox
                                    v-model="form.workflow_config.requestEditing.allowAfterExecutionStartWithoutApprovers"
                                    switch
                                    class="mb-0"
                                >
                                    Разрешить редактирование после взятия в работу, если согласующих нет
                                    <small class="text-muted d-block">
                                        Для маршрутов без согласующих: заявка останется редактируемой после старта выполнения.
                                    </small>
                                </b-form-checkbox>
                            </div>

                            <div v-if="!isSubmissionExtension" class="border rounded p-3 mt-3 bg-light" data-search-target="form-required-attachments">
                                <div class="d-flex justify-content-between align-items-center mb-2">
                                    <h6 class="mb-0">
                                        <i class="fas fa-paperclip"></i> Обязательные документы
                                    </h6>
                                    <b-button
                                        v-if="form.workflow_config.requiredAttachments.enabled"
                                        variant="outline-primary"
                                        size="sm"
                                        @click="addRequiredAttachmentRule"
                                    >
                                        <i class="fas fa-plus"></i> Добавить документ
                                    </b-button>
                                </div>

                                <b-form-checkbox v-model="form.workflow_config.requiredAttachments.enabled" switch>
                                    Требовать отдельные документы на этапах заявки
                                </b-form-checkbox>

                                <div
                                    v-if="form.workflow_config.requiredAttachments.enabled"
                                    class="mt-3"
                                >
                                    <div class="border rounded p-3 mb-3 bg-white">
                                        <h6 class="mb-2">
                                            <i class="fas fa-paperclip"></i> Быстрый просмотр документов
                                        </h6>
                                        <b-form-checkbox
                                            v-model="form.workflow_config.requiredAttachments.listPreview.enabled"
                                            switch
                                        >
                                            Показывать кнопку документов в общем списке заявок
                                        </b-form-checkbox>
                                        <small class="text-muted d-block mt-1">
                                            При включении в списке заявок появится иконка, раскрывающая приложенные обязательные документы без перехода внутрь заявки.
                                        </small>
                                    </div>

                                    <div
                                        v-for="(rule, index) in form.workflow_config.requiredAttachments.rules"
                                        :key="rule._uid || rule.key || index"
                                        class="required-attachment-rule border rounded p-3 mb-3 bg-white"
                                    >
                                        <div class="d-flex justify-content-between align-items-start mb-2">
                                            <b-form-checkbox v-model="rule.active" switch>
                                                Правило активно
                                            </b-form-checkbox>
                                            <b-button
                                                variant="outline-danger"
                                                size="sm"
                                                @click="removeRequiredAttachmentRule(index)"
                                            >
                                                <i class="fas fa-trash-alt"></i>
                                            </b-button>
                                        </div>

                                        <b-row>
                                            <b-col cols="12" md="4">
                                                <b-form-group label="Код документа">
                                                    <b-form-input
                                                        v-model="rule.key"
                                                        placeholder="invoice"
                                                    />
                                                    <small class="text-muted">Латиница без пробелов, например invoice или payment_order.</small>
                                                </b-form-group>
                                            </b-col>
                                            <b-col cols="12" md="4">
                                                <b-form-group label="Название">
                                                    <b-form-input
                                                        v-model="rule.label"
                                                        placeholder="Счёт на оплату"
                                                    />
                                                </b-form-group>
                                            </b-col>
                                            <b-col cols="12" md="4">
                                                <b-form-group label="Обязательно для">
                                                    <v-select
                                                        v-model="rule.requiredFor"
                                                        :options="requiredAttachmentTargetOptions"
                                                        label="text"
                                                        :reduce="option => option.value"
                                                        multiple
                                                        placeholder="Выберите этапы"
                                                    />
                                                </b-form-group>
                                            </b-col>
                                        </b-row>

                                        <b-row>
                                            <b-col cols="12" md="3">
                                                <b-form-group label="Минимум файлов">
                                                    <b-form-input
                                                        v-model.number="rule.minCount"
                                                        type="number"
                                                        min="1"
                                                    />
                                                </b-form-group>
                                            </b-col>
                                            <b-col cols="12" md="4">
                                                <b-form-group label="Кто прикладывает">
                                                    <v-select
                                                        v-model="rule.uploadRoles"
                                                        :options="requiredAttachmentUploadRoleOptions"
                                                        label="text"
                                                        :reduce="option => option.value"
                                                        multiple
                                                        placeholder="По этапу"
                                                    />
                                                    <small class="text-muted">
                                                        Если пусто, роли определятся по этапу.
                                                    </small>
                                                </b-form-group>
                                            </b-col>
                                            <b-col cols="12" md="5">
                                                <b-form-group label="Условие включения">
                                                    <b-form-checkbox v-model="rule.initiatorChoice" switch>
                                                        Инициатор выбирает при создании заявки
                                                    </b-form-checkbox>
                                                    <small class="text-muted">
                                                        Если включено, правило станет обязательным только для тех заявок,
                                                        где инициатор поставит соответствующую галочку.
                                                    </small>
                                                </b-form-group>
                                            </b-col>
                                        </b-row>
                                    </div>

                                    <div
                                        v-if="form.workflow_config.requiredAttachments.rules.length === 0"
                                        class="text-muted small"
                                    >
                                        Правил пока нет.
                                    </div>
                                </div>
                            </div>
                        </div>

                        <!-- Настройки справочника позиций -->
                        <div v-if="!isSubmissionExtension && !isReportAnnulment" class="mb-4" data-search-target="form-item-catalog">
                            <h6 class="mb-3 text-primary">
                                <i class="fas fa-book"></i> Справочник позиций
                            </h6>

                            <b-form-checkbox
                                :checked="itemCatalogEnabled"
                                switch
                                @change="setItemCatalogEnabled"
                            >
                                Подключить справочник к заявкам этого типа
                            </b-form-checkbox>
                            <small class="text-muted d-block mt-1">
                                Если выключено, заявка будет создаваться без табличного списка позиций.
                            </small>

                            <div class="border rounded p-3 mt-3 bg-light">
                                <h6 class="mb-2">
                                    <i class="fas fa-list-ul"></i> Быстрый просмотр позиций
                                </h6>
                                <b-form-checkbox
                                    v-model="form.workflow_config.listItemsPreview.enabled"
                                    :disabled="!itemCatalogEnabled"
                                    switch
                                >
                                    Показывать кнопку позиций в общем списке заявок
                                </b-form-checkbox>
                                <small class="text-muted d-block mt-1">
                                    При включении на карточке заявки появится иконка, раскрывающая список позиций без перехода внутрь заявки.
                                </small>
                                <small v-if="!itemCatalogEnabled" class="text-muted d-block mt-1">
                                    Сначала подключите справочник позиций.
                                </small>
                            </div>

                            <div v-if="itemCatalogEnabled" class="border rounded p-3 mt-3 bg-light">
                                <b-row>
                                    <b-col cols="12" md="6">
                                        <b-form-group label="Справочник" label-for="item-catalog-type">
                                            <v-select
                                                id="item-catalog-type"
                                                v-model="form.workflow_config.itemCatalog.type"
                                                :options="itemCatalogTypeOptions"
                                                label="text"
                                                :reduce="option => option.value"
                                                :clearable="false"
                                                placeholder="Выберите справочник"
                                                @input="onItemCatalogTypeChange"
                                            />
                                        </b-form-group>
                                    </b-col>
                                    <b-col cols="12" md="6">
                                        <b-form-group label="Название блока в заявке" label-for="item-catalog-label">
                                            <b-form-input
                                                id="item-catalog-label"
                                                v-model="form.workflow_config.itemCatalog.label"
                                                placeholder="Например: Спецодежда"
                                            />
                                        </b-form-group>
                                    </b-col>
                                </b-row>

                                <b-form-group
                                    v-if="form.workflow_config.itemCatalog.type === 'materials'"
                                    label="Группы материалов"
                                    label-for="item-catalog-material-groups"
                                >
                                    <v-select
                                        id="item-catalog-material-groups"
                                        v-model="form.workflow_config.itemCatalog.material_group_ids"
                                        :options="materialGroupOptions"
                                        label="text"
                                        :reduce="option => option.value"
                                        multiple
                                        placeholder="Все группы"
                                    />
                                    <small class="text-muted">
                                        Если выбраны, в заявке будут доступны только эти группы. Для отдельного справочника спецодежды выберите только группы спецодежды.
                                    </small>
                                </b-form-group>

                                <b-form-group
                                    v-if="form.workflow_config.itemCatalog.type === 'materials'"
                                    label="Запрещенные группы материалов"
                                    label-for="item-catalog-excluded-material-groups"
                                >
                                    <v-select
                                        id="item-catalog-excluded-material-groups"
                                        v-model="form.workflow_config.itemCatalog.excluded_material_group_ids"
                                        :options="materialGroupOptions"
                                        label="text"
                                        :reduce="option => option.value"
                                        multiple
                                        placeholder="Нет запрещенных групп"
                                    />
                                    <small class="text-muted">
                                        Эти группы не будут доступны в заявках этого типа, даже если весь справочник материалов подключен целиком.
                                    </small>
                                </b-form-group>

                                <div v-if="form.workflow_config.itemCatalog.type === 'materials'" class="border rounded p-3 mb-3 bg-white">
                                    <b-form-checkbox v-model="form.workflow_config.itemCatalog.ppe.enabled" switch>Получатели и размеры СИЗ</b-form-checkbox>
                                    <b-form-group v-if="form.workflow_config.itemCatalog.ppe.enabled" label="Группы материалов с размерами СИЗ" class="mt-2 mb-0">
                                        <v-select v-model="form.workflow_config.itemCatalog.ppe.material_group_ids" :options="materialGroupOptions"
                                            label="text" :reduce="option => option.value" multiple placeholder="Все разрешённые материалы" />
                                        <small class="text-muted">Пустой список — все разрешённые материалы. В ручных позициях блок включается отдельно.</small>
                                    </b-form-group>
                                </div>
                                <b-row v-if="form.workflow_config.itemCatalog.type === 'mechanic_assets'">
                                    <b-col cols="12" md="6">
                                        <b-form-group label="Видимость справочника" label-for="item-catalog-mechanic-scope">
                                            <v-select
                                                id="item-catalog-mechanic-scope"
                                                v-model="form.workflow_config.itemCatalog.scope"
                                                :options="mechanicAssetCatalogScopeOptions"
                                                label="text"
                                                :reduce="option => option.value"
                                                :clearable="false"
                                                @input="onMechanicAssetCatalogScopeChange"
                                            />
                                            <small class="text-muted">
                                                В режиме "Техника на объекте" объект в шапке заявки используется как источник. Объект назначения для перемещения выбирает исполнитель в строках отгрузки.
                                            </small>
                                        </b-form-group>
                                    </b-col>
                                </b-row>

                            </div>

                            <div v-if="employeeCatalogEnabled" class="border rounded p-3 mt-3 bg-light">
                                <h6 class="mb-2">
                                    <i class="fas fa-user-tag"></i> Сотрудники в заявке
                                </h6>
                                <b-form-checkbox
                                    v-model="form.workflow_config.itemCatalog.allow_custom_items"
                                    switch
                                    class="mb-2"
                                >
                                    Разрешить добавлять сотрудников вручную
                                </b-form-checkbox>
                                <small class="text-muted d-block mb-3">
                                    Если сотрудника нет в справочнике, инициатор сможет указать ФИО только для этой заявки.
                                </small>
                                <b-form-checkbox v-model="form.workflow_config.employeeCatalogSubject.enabled" switch>
                                    Добавлять выбранного сотрудника в наблюдатели и уведомлять о ходе согласования
                                </b-form-checkbox>
                                <small class="text-muted d-block mt-1">
                                    Для обходных листов: сотрудник, выбранный в таблице, станет наблюдателем заявки и будет получать отдельные уведомления о ходе согласования.
                                </small>
                            </div>

                        </div>

                        <!-- Настройки файлов -->
                        <div class="mb-4" data-search-target="form-files">
                            <h6 class="mb-3 text-primary">
                                <i class="fas fa-paperclip"></i> Настройки прикрепления файлов к заявке
                            </h6>
                            <b-row>
                                <b-col cols="12" md="6">
                                    <b-form-checkbox v-model="form.allow_attachments" switch>
                                        Разрешить прикрепление файлов к заявке
                                    </b-form-checkbox>
                                </b-col>
                                <b-col cols="12" md="6" v-if="form.allow_attachments && !isSubmissionExtension">
                                    <b-form-checkbox v-model="form.attachments_required" switch>
                                        Файлы обязательны
                                    </b-form-checkbox>
                                </b-col>
                            </b-row>

                            <b-row v-if="form.allow_attachments && !isSubmissionExtension" class="mt-3">
                                <b-col cols="12" md="4">
                                    <b-form-group label="Максимальный размер файла (МБ)" label-for="max-file-size">
                                        <b-form-input
                                            id="max-file-size"
                                            v-model.number="form.max_file_size"
                                            type="number"
                                            min="1"
                                            max="100"
                                            placeholder="10"
                                        />
                                        <small class="text-muted">По умолчанию: 10 МБ</small>
                                    </b-form-group>
                                </b-col>
                                <b-col cols="12" md="4">
                                    <b-form-group label="Максимальное количество файлов" label-for="max-files-count">
                                        <b-form-input
                                            id="max-files-count"
                                            v-model.number="form.max_files_count"
                                            type="number"
                                            min="1"
                                            max="20"
                                            placeholder="5"
                                        />
                                        <small class="text-muted">По умолчанию: 5 файлов</small>
                                    </b-form-group>
                                </b-col>
                                <b-col cols="12" md="4">
                                    <b-form-group label="Разрешенные типы файлов" label-for="allowed-file-types">
                                        <v-select
                                            id="allowed-file-types"
                                            v-model="form.allowed_file_types"
                                            :options="fileTypeOptions"
                                            label="text"
                                            :reduce="option => option.value"
                                            multiple
                                            placeholder="Выберите типы файлов"
                                        />
                                        <small class="text-muted">Оставьте пустым для всех типов</small>
                                    </b-form-group>
                                </b-col>
                            </b-row>
                        </div>

                        <!-- Настройки комментариев -->
                        <div class="mb-4" data-search-target="form-comments">
                            <h6 class="mb-3 text-primary">
                                <i class="fas fa-comments"></i> Настройки комментариев
                            </h6>
                            <b-row>
                                <b-col cols="12" md="6">
                                    <b-form-checkbox v-model="form.allow_comments" switch>
                                        Разрешить комментирование заявки
                                    </b-form-checkbox>
                                </b-col>
                                <b-col cols="12" md="6" v-if="form.allow_comments && !isSubmissionExtension">
                                    <b-form-checkbox v-model="form.allow_comment_attachments" switch>
                                        Разрешить прикрепление файлов к комментариям
                                    </b-form-checkbox>
                                </b-col>
                            </b-row>

                            <b-row v-if="form.allow_comments && !isSubmissionExtension" class="mt-3">
                                <b-col cols="12">
                                    <b-form-group label="Кто может комментировать" label-for="comment-permissions">
                                        <v-select
                                            id="comment-permissions"
                                            v-model="form.comment_permissions"
                                            :options="commentPermissionOptions"
                                            label="text"
                                            :reduce="option => option.value"
                                            placeholder="Выберите разрешения"
                                        />
                                    </b-form-group>
                                </b-col>
                            </b-row>

                            <b-row v-if="!isSubmissionExtension && form.allow_comments && form.allow_comment_attachments" class="mt-3">
                                <b-col cols="12" md="6">
                                    <b-form-group label="Макс. размер файла в комментарии (МБ)" label-for="max-comment-file-size">
                                        <b-form-input
                                            id="max-comment-file-size"
                                            v-model.number="form.max_comment_file_size"
                                            type="number"
                                            min="1"
                                            max="50"
                                            placeholder="5"
                                        />
                                        <small class="text-muted">По умолчанию: 5 МБ</small>
                                    </b-form-group>
                                </b-col>
                                <b-col cols="12" md="6">
                                    <b-form-group label="Макс. количество файлов в комментарии" label-for="max-comment-files">
                                        <b-form-input
                                            id="max-comment-files"
                                            v-model.number="form.max_comment_files_count"
                                            type="number"
                                            min="1"
                                            max="10"
                                            placeholder="3"
                                        />
                                        <small class="text-muted">По умолчанию: 3 файла</small>
                                    </b-form-group>
                                </b-col>
                            </b-row>
                        </div>
                    </b-tab>

                    <!-- Вкладка "Участники" -->
                    <b-tab v-if="isSectionAvailable('participants')" key="participants">
                        <template #title>
                            <i class="fas fa-users"></i>
                            <span class="d-none d-md-inline"> Участники</span>
                        </template>
                        
                        <div class="mb-4">
                            <h6 class="mb-3 text-primary">
                                <i class="fas fa-users"></i> Настройки участников процесса
                            </h6>
                            
                            <p v-if="isSubmissionExtension" class="small text-muted">Создать продление может пользователь с правом подачи связанного табеля. Минимум два согласующих принимают решения параллельно. Наблюдатели видят заявку и табель.</p>
                            <!-- Инициатор -->
                            <div v-if="!isSubmissionExtension" class="participant-config-panel participant-config-panel--initiator" data-search-target="participants-initiator">
                                <h6 class="participant-config-panel__title">
                                    <i class="fas fa-user-plus"></i> Инициатор (кто может создавать заявки)
                                </h6>
                                <MultipleParticipantsConfig
                                    v-model="form.initiator_config"
                                    :users="allUsers"
                                    :departments="allDepartments"
                                    participant-label="инициатора"
                                    :show-object-roles="true"
                                    :show-fallback-option="false"
                                    :show-priority="false"
                                    hint-text="Создать заявку сможет пользователь, который подходит хотя бы под одно правило."
                                    empty-text="Инициаторы не добавлены. По умолчанию тип доступен всем пользователям."
                                />
                            </div>

                            <!-- Согласующие -->
                            <div class="participant-config-panel participant-config-panel--approvers" data-search-target="participants-approvers">
                                <h6 class="participant-config-panel__title">
                                    <i class="fas fa-user-check"></i> Согласующие (кто согласовывает заявки)
                                </h6>
                                <MultipleParticipantsConfig
                                    v-model="form.approvers"
                                    :users="allUsers"
                                    :departments="allDepartments"
                                    participant-label="согласующего"
                                    :show-object-roles="true"
                                    :show-priority="!isSubmissionExtension"
                                    :show-fallback-option="!isSubmissionExtension"
                                    :hint-text="isSubmissionExtension ? 'Все найденные согласующие участвуют параллельно.' : null"
                                    :object-role-hint-text="isSubmissionExtension ? 'Роль определяется по объекту связанного табеля.' : null"
                                />
                            </div>

                            <!-- Наблюдатели -->
                            <div class="participant-config-panel participant-config-panel--observers" data-search-target="participants-observers">
                                <h6 class="participant-config-panel__title">
                                    <i class="fas fa-eye"></i> Наблюдатели (видят заявки и могут комментировать)
                                </h6>
                                <MultipleParticipantsConfig
                                    v-model="form.observers"
                                    :users="allUsers"
                                    :departments="allDepartments"
                                    participant-label="наблюдателя"
                                    :show-object-roles="true"
                                    :show-notification-mode="true"
                                    :show-priority="!isSubmissionExtension"
                                    :show-fallback-option="!isSubmissionExtension"
                                    :hint-text="isSubmissionExtension ? 'В состав войдут все найденные наблюдатели, кроме инициатора.' : null"
                                    :object-role-hint-text="isSubmissionExtension ? 'Роль определяется по объекту связанного табеля.' : null"
                                />
                            </div>

                            <!-- Исполнители -->
                            <div v-if="!isSubmissionExtension && !isReportAnnulment" class="participant-config-panel participant-config-panel--executors" data-search-target="participants-executors">
                                <h6 class="participant-config-panel__title">
                                    <i class="fas fa-user-cog"></i> Исполнители (кто выполняет заявки)
                                </h6>

                                <!-- Чекбокс "Назначается вручную" -->
                                <b-form-checkbox
                                    v-model="form.executor_assignment_manual"
                                    @change="onExecutorAssignmentModeChange"
                                    switch
                                    class="mb-3"
                                >
                                    Исполнитель назначается вручную
                                </b-form-checkbox>
                                <small class="text-muted d-block mb-3">
                                    Если включено, после согласования заявка перейдет в статус "Ожидание назначения исполнителя" (AWAITING_EXECUTOR), 
                                    и конкретный исполнитель должен быть назначен вручную уполномоченным лицом
                                </small>

                                <div class="participant-config-subpanel" data-search-target="participants-multi-executor">
                                    <h6 class="text-primary mb-3">
                                        <i class="fas fa-users-cog"></i> Мультиисполнители
                                    </h6>

                                    <b-form-checkbox
                                        v-model="form.workflow_config.multiExecutor.assignerEnabled"
                                        switch
                                        class="mb-3"
                                    >
                                        Разрешить распределение строк назначающим
                                        <small class="text-muted d-block">
                                            Для типов с ручным назначением: назначающий сможет включить режим нескольких исполнителей и распределить позиции между ними.
                                        </small>
                                    </b-form-checkbox>

                                    <b-form-checkbox
                                        v-model="form.workflow_config.multiExecutor.selfAssignEnabled"
                                        switch
                                        class="mb-0"
                                    >
                                        Разрешить построчное самоназначение исполнителями
                                        <small class="text-muted d-block">
                                            Для типов без назначающего: исполнитель сможет выбрать «Взять в работу отдельные строки» и назначать позиции на себя.
                                        </small>
                                    </b-form-checkbox>
                                </div>

                                <!-- Если назначается вручную - показываем настройку того, кто может назначать -->
                                <div v-if="form.executor_assignment_manual" class="participant-config-subpanel" data-search-target="participants-assigner">
                                    <h6 class="text-info mb-3">
                                        <i class="fas fa-user-shield"></i> Кто может назначать исполнителя
                                    </h6>
                                    <ParticipantConfig
                                        v-model="form.executor_assigner"
                                        :users="allUsers"
                                        :departments="allDepartments"
                                        participant-label="назначающего"
                                        :show-object-roles="false"
                                    />
                                </div>

                                <!-- Настройка пула исполнителей -->
                                <div v-if="!form.executor_assignment_manual" data-search-target="participants-executor-pool">
                                    <h6 class="small text-muted mb-2">Пул исполнителей (автоматическое назначение)</h6>
                                    <MultipleParticipantsConfig
                                        v-model="form.executors"
                                        :users="allUsers"
                                        :departments="allDepartments"
                                        participant-label="исполнителя"
                                        :show-department-head="true"
                                    />
                                </div>
                                <div v-else data-search-target="participants-executor-pool">
                                    <h6 class="small text-muted mb-2">Пул доступных исполнителей (для ручного назначения)</h6>
                                    <MultipleParticipantsConfig
                                        v-model="form.executors"
                                        :users="allUsers"
                                        :departments="allDepartments"
                                        participant-label="исполнителя"
                                        :show-department-head="false"
                                    />
                                    <small class="text-muted d-block mt-2">
                                        Из этого пула назначающий сможет выбрать конкретного исполнителя
                                    </small>
                                </div>
                            </div>
                        </div>
                    </b-tab>

                    <!-- Вкладка "Сроки" -->
                    <b-tab v-if="isSectionAvailable('deadlines')" key="deadlines">
                        <template #title>
                            <i class="fas fa-clock"></i>
                            <span class="d-none d-md-inline"> Сроки</span>
                        </template>
                        
                        <!-- Регламентные сроки -->
                        <div class="mb-4" data-search-target="deadlines-regulatory">
                            <h6 class="mb-3 text-primary">
                                <i class="fas fa-clock"></i> Регламентные сроки
                            </h6>
                            <b-row>
                                <b-col cols="12" md="6">
                                    <b-form-group label="Срок подачи заявки (дней)" label-for="submission-deadline">
                                        <b-form-input
                                            id="submission-deadline"
                                            v-model.number="form.submission_deadline_days"
                                            type="number"
                                            min="1"
                                            max="30"
                                            placeholder="4"
                                            :disabled="form.is_emergency"
                                        />
                                        <small class="text-muted">Количество рабочих дней до дедлайна</small>
                                    </b-form-group>
                                </b-col>
                                <b-col cols="12" md="6">
                                    <b-form-checkbox v-model="form.is_emergency" switch>
                                        Аварийная заявка (без ограничений по срокам)
                                    </b-form-checkbox>
                                </b-col>
                            </b-row>
                            
                            <b-row v-if="!form.is_emergency" class="mt-3">
                                <b-col cols="12">
                                    <b-form-checkbox v-model="form.requires_justification_if_late" switch>
                                        Требовать обоснование срочности при нарушении сроков
                                    </b-form-checkbox>
                                </b-col>
                            </b-row>

                            <!-- Дополнительное согласование при нарушении сроков -->
                            <b-row v-if="!form.is_emergency && form.requires_justification_if_late" class="mt-3">
                                <b-col cols="12">
                                    <b-form-checkbox v-model="form.late_approval_config.enabled" switch>
                                        Обязательно дополнительное согласование при нарушении сроков
                                    </b-form-checkbox>
                                    <small class="text-muted d-block mt-1">
                                        При нарушении сроков подачи заявки потребуется согласование дополнительных участников
                                    </small>
                                </b-col>
                            </b-row>

                            <!-- Блок выбора дополнительных согласующих -->
                            <div v-if="!form.is_emergency && form.late_approval_config.enabled" class="border p-3 mb-3 rounded bg-light mt-3" data-search-target="deadlines-late-approval">
                                <h6 class="text-warning mb-3">
                                    <i class="fas fa-user-shield"></i> Дополнительные согласующие при нарушении сроков
                                </h6>
                                <MultipleParticipantsConfig
                                    v-model="form.late_approval_config.approvers"
                                    :users="allUsers"
                                    :departments="allDepartments"
                                    participant-label="дополнительного согласующего"
                                    :show-object-roles="false"
                                />
                                <small class="text-muted d-block mt-2">
                                    <i class="fas fa-info-circle"></i>
                                    Эти участники будут добавлены к согласованию только при нарушении регламентных сроков подачи заявки
                                </small>
                            </div>

                            <div class="border p-3 mb-3 rounded bg-light mt-3" data-search-target="deadlines-editing">
                                <h6 class="text-primary mb-3">
                                    <i class="fas fa-calendar-alt"></i> Изменение срока выполнения
                                </h6>
                                <small class="text-muted d-block mb-3">
                                    Эти настройки относятся только к сроку выполнения и срочности. Общее редактирование заявки настраивается отдельно.
                                </small>

                                <h6 class="mb-2">До первого согласования</h6>
                                <MultipleParticipantsConfig
                                    v-model="form.workflow_config.deadlineEditing.beforeApproval"
                                    :users="allUsers"
                                    :departments="allDepartments"
                                    participant-label="редактора срока"
                                    :show-object-roles="true"
                                    :show-request-roles="true"
                                    :show-priority="false"
                                    :show-fallback-option="false"
                                    hint-text="До первого согласования срок смогут менять пользователи, подходящие хотя бы под одно правило."
                                    empty-text="Редакторы срока не добавлены. Срок до согласования сможет менять только администратор в God Mode."
                                    all-text="Любой пользователь системы сможет менять срок до первого согласования."
                                />

                                <h6 class="mb-2 mt-4">После первого согласования</h6>
                                <MultipleParticipantsConfig
                                    v-model="form.workflow_config.deadlineEditing.afterApproval"
                                    :users="allUsers"
                                    :departments="allDepartments"
                                    participant-label="редактора срока"
                                    :show-object-roles="true"
                                    :show-request-roles="true"
                                    :show-priority="false"
                                    :show-fallback-option="false"
                                    hint-text="После первого согласования срок смогут менять только пользователи, подходящие под эти правила. Администратор в God Mode может менять срок всегда."
                                    empty-text="Редакторы срока не добавлены. После первого согласования срок сможет менять только администратор в God Mode."
                                    all-text="Любой пользователь системы сможет менять срок после первого согласования."
                                />
                            </div>

                            <b-row v-if="form.is_emergency" class="mt-3">
                                <b-col cols="12">
                                    <b-alert variant="info" show>
                                        <i class="fas fa-info-circle"></i>
                                        <strong>Аварийные заявки:</strong> не имеют ограничений по срокам подачи, 
                                        но требуют обязательного указания причины поломки и фотоотчета повреждений.
                                    </b-alert>
                                </b-col>
                            </b-row>
                        </div>

                    </b-tab>

                    <!-- Вкладка "Уведомления" -->
                    <b-tab v-if="isSectionAvailable('notifications')" key="notifications">
                        <template #title>
                            <i class="fas fa-bell"></i>
                            <span class="d-none d-md-inline"> Уведомления</span>
                        </template>

                        <div class="mb-4" data-search-target="notifications-statuses">
                            <h6 class="mb-3 text-primary">
                                <i class="fas fa-bell"></i> Уведомления по статусам
                            </h6>
                            <StatusConfig v-model="form.status_config" :category="form.category" />
                        </div>

                        <div class="border rounded p-3 mb-3 bg-light" data-search-target="notifications-stale">
                            <h6 class="mb-2">
                                <i class="fas fa-hourglass-half"></i> Заявка без движения
                            </h6>
                            <b-form-checkbox
                                v-model="form.workflow_config.deadlinesConfig.staleRequestReminderEnabled"
                                switch
                                class="mb-3"
                            >
                                Включить напоминание, если заявка долго не меняется
                            </b-form-checkbox>
                            <b-row>
                                <b-col cols="12" md="4">
                                    <b-form-group
                                        label="Без движения, часов"
                                        label-for="stale-check-hours"
                                    >
                                        <b-form-input
                                            id="stale-check-hours"
                                            v-model.number="form.workflow_config.deadlinesConfig.staleCheckHours"
                                            type="number"
                                            min="1"
                                            :disabled="!form.workflow_config.deadlinesConfig.staleRequestReminderEnabled"
                                        />
                                    </b-form-group>
                                </b-col>
                            </b-row>
                        </div>

                        <div v-if="!isSubmissionExtension" class="border rounded p-3 mb-3 bg-light" data-search-target="notifications-excel">
                            <h6 class="mb-2">
                                <i class="fas fa-file-excel"></i> Excel в уведомлениях
                            </h6>
                            <b-form-checkbox
                                v-model="form.workflow_config.excelExport.enabled"
                                switch
                            >
                                Разрешить выгрузку позиций заявки в Excel
                            </b-form-checkbox>
                            <b-form-checkbox
                                v-if="form.workflow_config.excelExport.enabled"
                                v-model="form.workflow_config.excelExport.generateOnNotification"
                                switch
                                class="mt-2"
                            >
                                Прикладывать Excel к уведомлениям по заявке
                            </b-form-checkbox>
                            <small class="text-muted d-block mt-1">
                                Файл содержит все позиции заявки: материалы, топливные карты, технику и обычные строки.
                            </small>
                        </div>
                    </b-tab>

                    <!-- Вкладка "Маршрут" -->
                    <b-tab v-if="isSectionAvailable('workflow')" key="workflow">
                        <template #title>
                            <i class="fas fa-project-diagram"></i>
                            <span class="d-none d-md-inline"> Маршрут</span>
                        </template>

                        <div class="mb-4" data-search-target="workflow-config">
                            <h6 class="mb-3 text-primary">
                                <i class="fas fa-project-diagram"></i> Настройка workflow заявки
                            </h6>
                            <template v-if="isReportAnnulment">
                                <b-alert show variant="info">После согласования всеми участниками отчёты аннулируются, а заявка завершается. Объект обязателен. Изменить состав и причины можно в черновике или после возврата.</b-alert>
                                <b-form-checkbox v-model="form.workflow_config.sequentialApproval" switch>Последовательное согласование</b-form-checkbox>
                            </template>
                            <WorkflowConfig v-else
                                v-model="form.workflow_config"
                                :users="allUsers"
                                :departments="allDepartments"
                            />
                        </div>
                    </b-tab>

                    <!-- Вкладка "Автоматизация" -->
                    <b-tab v-if="isSectionAvailable('automation')" key="automation">
                        <template #title>
                            <i class="fas fa-bolt"></i>
                            <span class="d-none d-md-inline"> Автоматизация</span>
                        </template>

                        <RequestRecurrenceSettings :value="form.workflow_config.recurrence" :users="allUsers" :departments="allDepartments"
                            @input="$set(form.workflow_config, 'recurrence', $event)" />
                        <div class="border rounded p-3 mb-3 bg-light" data-search-target="automation-linked">
                            <div class="d-flex justify-content-between align-items-center mb-2">
                                <h6 class="mb-0">
                                    <i class="fas fa-link"></i> Связанные заявки
                                </h6>
                                <b-button
                                    v-if="form.workflow_config.linkedRequests.enabled"
                                    variant="outline-primary"
                                    size="sm"
                                    @click="addLinkedRequestRule"
                                >
                                    <i class="fas fa-plus"></i> Добавить правило
                                </b-button>
                            </div>

                            <b-form-checkbox v-model="form.workflow_config.linkedRequests.enabled" switch>
                                Разрешить создание связанных заявок из заявок этого типа
                            </b-form-checkbox>

                            <div
                                v-if="form.workflow_config.linkedRequests.enabled"
                                class="mt-3"
                            >
                                <div
                                    v-for="(rule, index) in form.workflow_config.linkedRequests.rules"
                                    :key="rule._uid || rule.key || index"
                                    class="linked-request-rule border rounded p-3 mb-3 bg-white"
                                >
                                    <div class="d-flex justify-content-between align-items-start mb-2">
                                        <b-form-checkbox v-model="rule.active" switch>
                                            Правило активно
                                        </b-form-checkbox>
                                        <b-button
                                            variant="outline-danger"
                                            size="sm"
                                            @click="removeLinkedRequestRule(index)"
                                        >
                                            <i class="fas fa-trash-alt"></i>
                                        </b-button>
                                    </div>

                                    <b-row>
                                        <b-col cols="12" md="4">
                                            <b-form-group label="Название кнопки">
                                                <b-form-input
                                                    v-model="rule.label"
                                                    placeholder="Создать оплату"
                                                />
                                            </b-form-group>
                                        </b-col>
                                        <b-col cols="12" md="4">
                                            <b-form-group label="Целевой тип заявки">
                                                <v-select
                                                    v-model="rule.targetRequestTypeId"
                                                    :options="linkedRequestTypeOptions"
                                                    label="text"
                                                    :reduce="option => option.value"
                                                    placeholder="Выберите тип"
                                                />
                                            </b-form-group>
                                        </b-col>
                                        <b-col cols="12" md="4">
                                            <b-form-group label="Шаблон названия">
                                                <b-form-input
                                                    v-model="rule.titleTemplate"
                                                    placeholder="{targetType} по заявке {sourceNumber}"
                                                />
                                            </b-form-group>
                                        </b-col>
                                    </b-row>

                                    <b-row>
                                        <b-col cols="12" md="6">
                                            <b-form-group label="Кому доступна кнопка">
                                                <b-form-checkbox-group
                                                    v-model="rule.allowedRoles"
                                                    :options="linkedRequestRoleOptions"
                                                    stacked
                                                />
                                            </b-form-group>
                                        </b-col>
                                        <b-col cols="12" md="6">
                                            <b-form-group label="Статусы исходной заявки">
                                                <v-select
                                                    v-model="rule.allowedStatuses"
                                                    :options="linkedRequestStatusOptions"
                                                    label="text"
                                                    :reduce="option => option.value"
                                                    multiple
                                                    placeholder="Любой статус"
                                                />
                                            </b-form-group>
                                        </b-col>
                                    </b-row>

                                    <b-row>
                                        <b-col cols="12" md="4">
                                            <b-form-group label="Подпись в исходной">
                                                <b-form-input
                                                    v-model="rule.sourceLabel"
                                                    placeholder="Заявка на оплату"
                                                />
                                            </b-form-group>
                                        </b-col>
                                        <b-col cols="12" md="4">
                                            <b-form-group label="Подпись в созданной">
                                                <b-form-input
                                                    v-model="rule.targetLabel"
                                                    placeholder="Основание"
                                                />
                                            </b-form-group>
                                        </b-col>
                                        <b-col cols="12" md="4">
                                            <b-form-group label="Данные для черновика">
                                                <b-form-checkbox v-model="rule.copyBuildingProcess" switch>
                                                    Перенести объект и договор
                                                </b-form-checkbox>
                                            </b-form-group>
                                        </b-col>
                                    </b-row>

                                    <div class="linked-request-access-options border-top pt-3 mt-2">
                                        <h6 class="small text-muted mb-2">Перенос команды между связанными заявками</h6>
                                        <b-row>
                                            <b-col cols="12" md="6">
                                                <b-form-group
                                                    label="Из созданной заявки в основание"
                                                    label-class="small font-weight-bold"
                                                >
                                                    <b-form-checkbox-group
                                                        v-model="rule.transferTargetTeamRolesToSourceObservers"
                                                        :options="linkedRequestTeamRoleOptions"
                                                        stacked
                                                    />
                                                    <small class="text-muted d-block mt-1">
                                                        Выбранные роли созданной заявки будут добавлены наблюдателями в основание без уведомлений, если их там еще нет.
                                                    </small>
                                                </b-form-group>
                                            </b-col>
                                            <b-col cols="12" md="6">
                                                <b-form-group
                                                    label="Из основания в созданную заявку"
                                                    label-class="small font-weight-bold"
                                                >
                                                    <b-form-checkbox-group
                                                        v-model="rule.transferSourceTeamRolesToTargetObservers"
                                                        :options="linkedRequestTeamRoleOptions"
                                                        stacked
                                                    />
                                                    <small class="text-muted d-block mt-1">
                                                        Выбранные роли основания будут добавлены наблюдателями в созданную заявку без уведомлений, если их там еще нет.
                                                    </small>
                                                </b-form-group>
                                            </b-col>
                                        </b-row>
                                    </div>
                                </div>

                                <div
                                    v-if="form.workflow_config.linkedRequests.rules.length === 0"
                                    class="text-muted small"
                                >
                                    Правил пока нет.
                                </div>
                            </div>
                        </div>

                        <div v-if="fuelCardCatalogEnabled" class="border rounded p-3 mb-3 bg-light" data-search-target="automation-fuel-card">
                            <h6 class="mb-2">
                                <i class="fas fa-gas-pump"></i> Действие со справочником топливных карт
                            </h6>
                            <b-form-checkbox v-model="form.workflow_config.fuelCardAction.enabled" switch>
                                Выполнять действие с топливной картой при переходе заявки в заданный статус
                            </b-form-checkbox>
                            <small class="text-muted d-block mt-1">
                                В истории карты будет записано, на основании какой заявки произошло изменение.
                            </small>

                            <b-row v-if="form.workflow_config.fuelCardAction.enabled" class="mt-3">
                                <b-col cols="12" md="6">
                                    <b-form-group label="Когда применять" label-for="fuel-card-action-trigger">
                                        <v-select
                                            id="fuel-card-action-trigger"
                                            v-model="form.workflow_config.fuelCardAction.triggerStatus"
                                            :options="fuelCardTriggerStatusOptions"
                                            label="text"
                                            :reduce="option => option.value"
                                            :clearable="false"
                                        />
                                    </b-form-group>
                                </b-col>
                                <b-col cols="12" md="6">
                                    <b-form-group label="Что сделать с картой" label-for="fuel-card-action-operation">
                                        <v-select
                                            id="fuel-card-action-operation"
                                            v-model="form.workflow_config.fuelCardAction.operation"
                                            :options="fuelCardOperationOptions"
                                            label="text"
                                            :reduce="option => option.value"
                                            :clearable="false"
                                        />
                                    </b-form-group>
                                </b-col>
                            </b-row>
                        </div>

                        <div v-if="mechanicAssetCatalogEnabled" class="border rounded p-3 mb-3 bg-light" data-search-target="automation-mechanic-assets">
                            <h6 class="mb-2">
                                <i class="fas fa-tools"></i> Действие со справочником механиков
                            </h6>
                            <b-form-checkbox
                                v-model="form.workflow_config.mechanicAssetAction.enabled"
                                switch
                                @change="onMechanicAssetActionEnabledChange"
                            >
                                Выполнять действие с техникой при переходе заявки в заданный статус
                            </b-form-checkbox>
                            <small class="text-muted d-block mt-1">
                                После выполнения заявки справочник техники изменится автоматически, а в истории позиции появится ссылка на заявку.
                            </small>
                            <b-form-group label="Режим таблицы в заявке" class="mt-3 mb-0">
                                <v-select
                                    v-model="form.workflow_config.mechanicAssetTableMode"
                                    :options="mechanicAssetTableModeOptions"
                                    label="text"
                                    :reduce="option => option.value"
                                    :clearable="false"
                                />
                            </b-form-group>

                            <div v-if="form.workflow_config.mechanicAssetAction.enabled" class="mt-3">
                                <div
                                    v-for="(action, index) in form.workflow_config.mechanicAssetActions"
                                    :key="action._uid || index"
                                    class="mechanic-action-rule"
                                >
                                    <div class="d-flex align-items-center justify-content-between mb-2">
                                        <strong>Действие {{ index + 1 }}</strong>
                                        <b-button
                                            v-if="form.workflow_config.mechanicAssetActions.length > 1"
                                            variant="outline-danger"
                                            size="sm"
                                            @click="removeMechanicAssetAction(index)"
                                        >
                                            <i class="fas fa-times"></i>
                                        </b-button>
                                    </div>
                                    <b-row>
                                        <b-col cols="12" md="6">
                                            <b-form-group :label="index === 0 ? 'Когда применять' : null">
                                                <v-select
                                                    v-model="action.triggerStatus"
                                                    :options="mechanicAssetTriggerStatusOptions"
                                                    label="text"
                                                    :reduce="option => option.value"
                                                    :clearable="false"
                                                    @input="syncPrimaryMechanicAssetAction"
                                                />
                                            </b-form-group>
                                        </b-col>
                                        <b-col cols="12" md="6">
                                            <b-form-group :label="index === 0 ? 'Что сделать с техникой' : null">
                                                <v-select
                                                    v-model="action.operation"
                                                    :options="mechanicAssetOperationOptions"
                                                    label="text"
                                                    :reduce="option => option.value"
                                                    :clearable="false"
                                                    @input="syncPrimaryMechanicAssetAction"
                                                />
                                            </b-form-group>
                                        </b-col>
                                    </b-row>
                                    <b-row>
                                        <b-col cols="12" class="mb-2">
                                            <b-form-checkbox v-model="action.markInTransferOnShipment" switch @change="syncPrimaryMechanicAssetAction">
                                                При отгрузке отмечать технику как перемещается
                                            </b-form-checkbox>
                                            <small class="text-muted d-block">
                                                В справочнике появится статус перемещения и направление от текущего места к целевому.
                                            </small>
                                        </b-col>
                                        <b-col cols="12" md="6">
                                            <b-form-checkbox v-model="action.requireMeterValue" switch @change="syncPrimaryMechanicAssetAction">
                                                Требовать пробег или моточасы
                                            </b-form-checkbox>
                                        </b-col>
                                        <b-col cols="12" md="6">
                                            <b-form-checkbox v-model="action.requireCondition" switch @change="syncPrimaryMechanicAssetAction">
                                                Требовать состояние техники
                                            </b-form-checkbox>
                                        </b-col>
                                    </b-row>
                                </div>
                                <b-button variant="outline-primary" size="sm" class="mt-2" @click="addMechanicAssetAction">
                                    <i class="fas fa-plus"></i> Добавить действие
                                </b-button>
                            </div>
                        </div>

                        <div class="border rounded p-3 mb-3 bg-light" data-search-target="automation-fulfillment">
                            <h6>Отгрузка и приёмка</h6>
                            <b-form-checkbox v-model="form.workflow_config.fulfillment.shippingRequired" switch>Исполнитель указывает, сколько отгрузил и что ещё довезёт</b-form-checkbox>
                            <b-form-checkbox v-model="form.workflow_config.fulfillment.acceptanceRequired" switch>Принимающий указывает, сколько получил</b-form-checkbox>
                            <small class="text-muted">Если ничего не отгрузили или не получили, нужно вписать 0. Если количество отличается, нужна причина. Её можно написать один раз для всей заявки или отдельно по строкам. Просроченные заявки попадают в «Требует разбора».</small>
                        </div>
                        <div class="border rounded p-3 mb-3 bg-light" data-search-target="automation-auto-close">
                            <h6 class="mb-2">
                                <i class="fas fa-hourglass-end"></i> Автозакрытие приемки
                            </h6>
                            <b-form-checkbox
                                v-model="form.workflow_config.deadlinesConfig.autoCloseAwaitingAcceptanceEnabled"
                                switch
                                class="mb-3"
                            >
                                Закрывать заявку автоматически, если инициатор не принял результат
                            </b-form-checkbox>
                            <b-row>
                                <b-col cols="12" md="4">
                                    <b-form-group
                                        label="Через, календарных дней"
                                        label-for="auto-close-awaiting-acceptance-days"
                                    >
                                        <b-form-input
                                            id="auto-close-awaiting-acceptance-days"
                                            v-model.number="form.workflow_config.deadlinesConfig.autoCloseAwaitingAcceptanceDays"
                                            type="number"
                                            min="1"
                                            :disabled="!form.workflow_config.deadlinesConfig.autoCloseAwaitingAcceptanceEnabled"
                                        />
                                        <small class="text-muted">{{ form.workflow_config.fulfillment.shippingRequired || form.workflow_config.fulfillment.acceptanceRequired ? 'Заявка закроется, когда исполнитель закончит работу и укажет, что больше не довезёт. Сначала придёт предупреждение. После него на приёмку будет не меньше суток. Количества не изменятся.' : 'Сработает только когда все позиции полностью отгружены.' }}</small>
                                    </b-form-group>
                                </b-col>
                            </b-row>
                        </div>
                    </b-tab>

                    <!-- Вкладка "Дополнительно" -->
                    <b-tab v-if="isSectionAvailable('customFields')" key="customFields">
                        <template #title>
                            <i class="fas fa-list-ul"></i>
                            <span class="d-none d-md-inline"> Дополнительно</span>
                        </template>
                        
                        <div class="mb-4 custom-fields-editor" data-search-target="custom-fields">
                            <div class="custom-fields-editor__header">
                                <div>
                                    <h6 class="mb-1 text-primary">
                                        <i class="fas fa-list-ul"></i> Дополнительные поля в заявке
                                    </h6>
                                    <div class="custom-fields-editor__counter">
                                        {{ (form.custom_fields || []).length }} полей
                                    </div>
                                </div>
                                <b-button
                                    variant="outline-primary"
                                    size="sm"
                                    @click="addCustomField"
                                >
                                    <i class="fas fa-plus"></i> Добавить поле
                                </b-button>
                            </div>
                            
                            <div v-if="form.custom_fields && form.custom_fields.length" class="custom-fields-editor__list">
                                <CustomFieldConfig
                                    v-for="(field, index) in form.custom_fields"
                                    :key="field._uid || index"
                                    v-model="form.custom_fields[index]"
                                    :index="index"
                                    :is-first="index === 0"
                                    :is-last="index === form.custom_fields.length - 1"
                                    :available-fields="form.custom_fields.slice(0, index)"
                                    :reserved-keys="getReservedCustomFieldKeys(index)"
                                    @move-up="moveCustomField(index, -1)"
                                    @move-down="moveCustomField(index, 1)"
                                    @remove="removeCustomField(index)"
                                />
                            </div>
                            <div v-else class="custom-fields-editor__empty">
                                <i class="fas fa-info-circle"></i>
                                Дополнительные поля не добавлены
                            </div>
                        </div>
                    </b-tab>
                    </b-tabs>
                </main>
            </div>
        </b-form>
    </div>
</template>

<script>
import annulmentProfile from '../../../../../server/utils/Requests/reportAnnulmentProfile';
import RequestRecurrenceSettings from '../components/RequestRecurrenceSettings.vue';
import extensionProfile from '../../../../../server/utils/Requests/timesheetExtensionProfile';
import ParticipantConfig from './ParticipantConfig/ParticipantConfig.vue';
import MultipleParticipantsConfig from './MultipleParticipantsConfig/MultipleParticipantsConfig.vue';
import CustomFieldConfig from './CustomFieldConfig/CustomFieldConfig.vue';
import StatusConfig from './StatusConfig/StatusConfig.vue';
import WorkflowConfig from './WorkflowConfig/WorkflowConfig.vue';
import { REQUEST_CATEGORIES } from '@/constants/requestCategories';
import { CARD_BORDER_VARIANTS, normalizeCardBorderVariant } from '@/constants/requestCardColors';
import {
    DEFAULT_REQUEST_TYPE_ICON,
    REQUEST_TYPE_ICON_OPTIONS,
    normalizeRequestTypeIcon
} from '@/constants/requestTypeIcons';
import { getAllStatuses } from '@/utils/requestStatuses';
import { DEFAULT_CONFIGURABLE_COMMENT_ACTIONS } from '@/utils/requestClosingComment';
import {
    normalizeRequiredAttachmentRule as normalizeRequiredAttachmentRuleConfig
} from '@/utils/requestRequiredAttachments';

function cloneJson(value, fallback) {
    const source = value === undefined || value === null ? fallback : value;
    return JSON.parse(JSON.stringify(source));
}

function getDefaultInitiatorConfig() {
    return {
        participants: [
            {
                type: 'all',
                priority: 0
            }
        ],
        allow_any_department_employee: false
    };
}

function getDefaultDeadlineEditingConfig() {
    return {
        beforeApproval: {
            participants: [
                {
                    type: 'request_role',
                    roles: ['initiator', 'approver'],
                    priority: 0
                }
            ],
            allow_any_department_employee: false
        },
        afterApproval: {
            participants: [],
            allow_any_department_employee: false
        }
    };
}

export default {
    name: "RequestTypeForm",
    components: {
        RequestRecurrenceSettings,
        ParticipantConfig,
        MultipleParticipantsConfig,
        CustomFieldConfig,
        StatusConfig,
        WorkflowConfig
    },
    data() {
        return {
            editMode: false,
            saving: false,
            activeSectionIndex: 0,
            settingsSearchQuery: '',
            highlightedSearchTargetKey: null,
            highlightedSearchElement: null,
            searchHighlightMarks: [],
            searchBubbleElements: [],
            searchBubbleMouseMoveHandler: null,
            form: {
                name: '',
                category: null,
                description: '',
                is_active: true,
                initiator_config: getDefaultInitiatorConfig(),
                approvers: {
                    participants: [],
                    allow_any_department_employee: false
                },
                observers: {
                    participants: [],
                    allow_any_department_employee: false
                },
                executors: {
                    participants: [],
                    allow_any_department_employee: false
                },
                executor_assignment_manual: false,
                executor_assigner: {
                    type: 'all'
                },
                custom_fields: [],
                allow_attachments: true,
                attachments_required: false,
                max_file_size: 10,
                max_files_count: 5,
                allowed_file_types: [],
                allow_comments: true,
                comment_permissions: 'all_participants',
                allow_comment_attachments: true,
                max_comment_file_size: 5,
                max_comment_files_count: 3,
                submission_deadline_days: 4,
                is_emergency: false,
                requires_justification_if_late: true,
                requires_title: true,
                status_config: {},
                workflow_config: {
                    cardBorderVariant: 'primary',
                    requestTypeIcon: DEFAULT_REQUEST_TYPE_ICON,
                    buildingProcess: {
                        enabled: false,
                        required: false
                    },
                    requiresBuildingProcess: false,
                    excelExport: {
                        enabled: true,
                        generateOnNotification: false
                    },
                    requestEditing: {
                        allowAfterApproval: false,
                        allowAfterExecutionStartWithoutApprovers: false
                    },
                    fulfillment: { shippingRequired: false, acceptanceRequired: false },
                    closingComment: {
                        required: false,
                        requiredActions: [...DEFAULT_CONFIGURABLE_COMMENT_ACTIONS]
                    },
                    deadlineEditing: getDefaultDeadlineEditingConfig(),
                    multiExecutor: {
                        assignerEnabled: true,
                        selfAssignEnabled: true
                    },
                    employeeCatalogSubject: {
                        enabled: false
                    },
                    fuelCardAction: {
                        enabled: false,
                        triggerStatus: 'completed',
                        operation: 'assign_to_initiator'
                    },
                    mechanicAssetAction: {
                        enabled: false,
                        triggerStatus: 'completed',
                        operation: 'move_to_object',
                        markInTransferOnShipment: false,
                        requireMeterValue: false,
                        requireCondition: false
                    },
                    mechanicAssetActions: [],
                    mechanicAssetTableMode: 'movement',
                    listItemsPreview: {
                        enabled: false
                    },
                    deadlinesConfig: {
                        staleRequestReminderEnabled: false,
                        staleCheckHours: 24,
                        autoCloseAwaitingAcceptanceEnabled: false,
                        autoCloseAwaitingAcceptanceDays: 3
                    },
                    linkedRequests: {
                        enabled: false,
                        rules: []
                    },
                    requiredAttachments: {
                        enabled: false,
                        listPreview: {
                            enabled: false
                        },
                        rules: []
                    },
                    itemCatalog: {
                        ppe: {enabled: false, material_group_ids: []},
                        enabled: false,
                        type: 'materials',
                        label: 'Материалы',
                        material_group_ids: [],
                        excluded_material_group_ids: []
                    },
                    descriptionField: {
                        enabled: true,
                        required: false
                    }
                },
                late_approval_config: {
                    enabled: false,
                    approvers: {
                        participants: [],
                        allow_any_department_employee: false
                    }
                }
            },
            fileTypeOptions: [
                { value: 'image', text: 'Изображения (jpg, png, gif)' },
                { value: 'pdf', text: 'PDF документы' },
                { value: 'doc', text: 'Word документы (doc, docx)' },
                { value: 'xls', text: 'Excel таблицы (xls, xlsx)' },
                { value: 'archive', text: 'Архивы (zip, rar)' },
                { value: 'text', text: 'Текстовые файлы (txt, csv)' }
            ],
            commentPermissionOptions: [
                { value: 'all_participants', text: 'Все участники заявки (инициатор, согласующие, наблюдатели, исполнители, назначающие)' },
                { value: 'initiator_and_executors', text: 'Только инициатор и исполнители' },
                { value: 'executors_only', text: 'Только исполнители' },
                { value: 'all_users', text: 'Все пользователи системы' }
            ],
            categoryOptions: REQUEST_CATEGORIES.map(({ value, text }) => ({ value, text })),
            itemCatalogTypeOptions: [
                { value: 'materials', text: 'Материалы' },
                { value: 'employees', text: 'Сотрудники' },
                { value: 'fuel_cards', text: 'Топливные карты' },
                { value: 'mechanic_assets', text: 'Техника отдела механиков' }
            ],
            mechanicAssetCatalogScopeOptions: [
                { value: 'all', text: 'Вся техника' },
                { value: 'mechanic_assets_on_object', text: 'Техника на объекте' }
            ],
            fuelCardTriggerStatusOptions: [
                { value: 'completed', text: 'Когда заявка завершена' },
                { value: 'awaiting_acceptance', text: 'Когда заявка ожидает приемку' },
                { value: 'partially_completed', text: 'Когда заявка частично выполнена' }
            ],
            fuelCardOperationOptions: [
                { value: 'assign_to_initiator', text: 'Выдать инициатору заявки' },
                { value: 'assign_to_action_user', text: 'Выдать пользователю, выполнившему действие' },
                { value: 'issue_to_recipient', text: 'Выдать выбранному получателю (карту выбирает исполнитель)' },
                { value: 'move_to_recipient', text: 'Перемещение топливной карты' },
                { value: 'change_limits', text: 'Изменить суточные лимиты топлива по карте' },
                { value: 'attach_to_request_object', text: 'Привязать к объекту заявки' },
                { value: 'return_card', text: 'Зафиксировать сдачу' },
                { value: 'block_card', text: 'Заблокировать' },
                { value: 'mark_lost', text: 'Зафиксировать утерю' }
            ],
            mechanicAssetTriggerStatusOptions: [
                { value: 'in_progress', text: 'Когда заявка взята в работу' },
                { value: 'mechanic_repair_accepted', text: 'Когда в таблице приняли в ремонт' },
                { value: 'completed', text: 'Когда заявка завершена' },
                { value: 'awaiting_acceptance', text: 'Когда заявка ожидает приемку' },
                { value: 'partially_completed', text: 'Когда заявка частично выполнена' }
            ],
            mechanicAssetTableModeOptions: [
                { value: 'movement', text: 'Выдача / перемещение техники' },
                { value: 'repair', text: 'Ремонт техники и инструмента' }
            ],
            mechanicAssetOperationOptions: [
                { value: 'issue_to_object', text: 'Выдать технику на объект' },
                { value: 'move_to_object', text: 'Переместить технику на объект' },
                { value: 'move_to_warehouse', text: 'Переместить технику на склад/ремзону' },
                { value: 'return_to_warehouse', text: 'Вернуть технику на склад/ремзону' },
                { value: 'accept_to_repair', text: 'Принять технику в ремонт' },
                { value: 'finish_repair', text: 'Завершить ремонт техники' },
                { value: 'rent_out', text: 'Сдать технику в аренду' },
                { value: 'return_from_rent', text: 'Вернуть технику из аренды' }
            ],
            cardBorderVariants: CARD_BORDER_VARIANTS,
            requestTypeIconOptions: REQUEST_TYPE_ICON_OPTIONS,
            materialGroupOptions: [],
            allUsers: [],
            allDepartments: [],
            requestTypesForLinks: [],
            linkedRequestRoleOptions: [
                { value: 'initiator', text: 'Инициатор' },
                { value: 'assigner', text: 'Назначающий' },
                { value: 'executor', text: 'Исполнитель' },
                { value: 'approver', text: 'Согласующий' },
                { value: 'observer', text: 'Наблюдатель' }
            ],
            linkedRequestTeamRoleOptions: [
                { value: 'initiator', text: 'Инициаторы' },
                { value: 'approver', text: 'Согласующие' },
                { value: 'assigner', text: 'Назначающие' },
                { value: 'executor', text: 'Исполнители' },
                { value: 'observer', text: 'Наблюдатели' }
            ],
            linkedRequestStatusOptions: getAllStatuses().map(status => ({
                value: status.key,
                text: status.label
            })),
            requiredAttachmentTargetOptions: [
                { value: 'create', text: 'Создание заявки' },
                { value: 'submit', text: 'Отправка заявки' },
                { value: 'approve', text: 'Согласование' },
                { value: 'reject', text: 'Отклонение' },
                { value: 'return', text: 'Возврат' },
                { value: 'assign', text: 'Назначение исполнителя' },
                { value: 'execute', text: 'Взятие в работу' },
                { value: 'complete', text: 'Завершение выполнения' },
                { value: 'partially_complete', text: 'Частичное выполнение' },
                { value: 'accept', text: 'Приемка' },
                { value: 'accept_partially', text: 'Частичная приемка' },
                { value: 'cancel', text: 'Отмена' }
            ],
            requiredAttachmentUploadRoleOptions: [
                { value: 'initiator', text: 'Инициатор' },
                { value: 'assigner', text: 'Назначающий' },
                { value: 'executor', text: 'Исполнитель' },
                { value: 'approver', text: 'Согласующий' },
                { value: 'observer', text: 'Наблюдатель' },
                { value: 'all', text: 'Все участники' }
            ]
        }
    },
    computed: {
        isReportAnnulment() { return this.form.category === 'report_annulment'; },
        isSubmissionExtension() { return extensionProfile.isExtension(this.form.category); },
        requestTypeSections() {
            return [
                {
                    key: 'passport',
                    title: 'Паспорт',
                    icon: 'fas fa-id-card',
                    description: 'Название, категория, активность и внешний вид типа заявки.',
                    summary: this.getBasicsSectionSummary(),
                    issueCount: this.getSectionIssueCount('passport')
                },
                {
                    key: 'form',
                    title: 'Форма заявки',
                    icon: 'fas fa-clipboard-list',
                    description: 'Поля, объект, справочник позиций, документы, файлы и комментарии.',
                    summary: this.getFormSectionSummary(),
                    issueCount: this.getSectionIssueCount('form')
                },
                {
                    key: 'participants',
                    title: 'Участники',
                    icon: 'fas fa-users',
                    description: 'Кто создает, согласует, наблюдает, назначает и выполняет заявки этого типа.',
                    summary: this.getParticipantsSectionSummary(),
                    issueCount: this.getSectionIssueCount('participants')
                },
                {
                    key: 'deadlines',
                    title: 'Сроки',
                    icon: 'fas fa-clock',
                    description: 'Регламент подачи, срочность и права на изменение срока выполнения.',
                    summary: this.getDeadlinesSectionSummary(),
                    issueCount: this.getSectionIssueCount('deadlines')
                },
                {
                    key: 'notifications',
                    title: 'Уведомления',
                    icon: 'fas fa-bell',
                    description: 'Кому и когда отправляются уведомления, напоминания и Excel-вложения.',
                    summary: this.getNotificationsSectionSummary(),
                    issueCount: this.getSectionIssueCount('notifications')
                },
                {
                    key: 'workflow',
                    title: 'Маршрут',
                    icon: 'fas fa-project-diagram',
                    description: 'Действия, переходы, права и системная логика маршрута заявки.',
                    summary: this.getWorkflowSectionSummary(),
                    issueCount: this.getSectionIssueCount('workflow')
                },
                {
                    key: 'automation',
                    title: 'Автоматизация',
                    icon: 'fas fa-bolt',
                    description: 'Связанные заявки, действия со справочниками и автозакрытие приемки.',
                    summary: this.getAutomationSummary(),
                    issueCount: this.getSectionIssueCount('automation')
                },
                {
                    key: 'customFields',
                    title: 'Доп. поля',
                    icon: 'fas fa-list-ul',
                    description: 'Дополнительные поля, которые пользователь заполняет в заявке.',
                    summary: this.getCustomFieldsSectionSummary(),
                    issueCount: this.getSectionIssueCount('customFields')
                }
            ].filter(section => this.isSectionAvailable(section.key)).map(section => {
                if (!this.isSubmissionExtension) return section;
                const descriptions = {form: 'Файлы и комментарии к продлению.', participants: 'Согласующие и наблюдатели связанного табеля.', notifications: 'Создание, итоговое решение и напоминания без движения.'};
                return Object.assign({}, section, {description: descriptions[section.key] || section.description});
            });
        },
        normalizedSettingsSearchQuery() {
            return this.normalizeSettingsSearchText(this.settingsSearchQuery);
        },
        settingsSearchTokens() {
            return this.normalizedSettingsSearchQuery
                .split(/\s+/)
                .filter(Boolean);
        },
        searchableRequestTypeSections() {
            const tokens = this.settingsSearchTokens;
            if (!tokens.length) {
                return this.requestTypeSections.map(section => Object.assign({}, section, {
                    searchSummary: section.summary,
                    searchResults: []
                }));
            }

            return this.requestTypeSections
                .map(section => {
                    const searchParameters = this.getSectionSearchParameters(section.key);
                    const baseTerms = [
                        section.title,
                        section.description,
                        section.summary
                    ];
                    const matchedResults = searchParameters
                        .filter(result => this.matchesSettingsSearch(this.getSearchParameterText(result), tokens));
                    const allTerms = baseTerms.concat(
                        searchParameters.map(result => this.getSearchParameterText(result))
                    );
                    const sectionMatched = baseTerms.some(term => this.matchesSettingsSearch(term, tokens)) ||
                        this.matchesSettingsSearch(allTerms.join(' '), tokens);

                    if (!sectionMatched && matchedResults.length === 0) {
                        return null;
                    }

                    const searchResults = matchedResults.length
                        ? matchedResults
                        : [this.createSearchParameter(section.title, this.getSectionDefaultTargetKey(section.key), [section.description])];
                    const visibleMatches = searchResults.slice(0, 3).map(result => result.label);
                    const extraCount = searchResults.length - visibleMatches.length;
                    const searchSummary = visibleMatches.length
                        ? `${visibleMatches.join(', ')}${extraCount > 0 ? ` +${extraCount}` : ''}`
                        : section.description;

                    return Object.assign({}, section, {
                        searchSummary,
                        searchResults
                    });
                })
                .filter(Boolean);
        },
        searchResultsLabel() {
            const count = this.searchableRequestTypeSections.reduce((sum, section) => {
                return sum + (Array.isArray(section.searchResults) ? section.searchResults.length : 0);
            }, 0);
            if (count === 0) {
                return 'Ничего не найдено';
            }
            return `Найдено: ${count} ${this.pluralizeRu(count, 'параметр', 'параметра', 'параметров')}`;
        },
        activeRequestTypeSection() {
            return this.requestTypeSections[this.activeSectionIndex] || this.requestTypeSections[0] || {};
        },
        requestTypeSummary() {
            if (this.isSubmissionExtension) return [
                {key: "category", label: "Категория", value: "Табели", icon: "fas fa-calendar-alt"},
                {key: "participants", label: "Участники", value: this.getParticipantsSectionSummary(), icon: "fas fa-users"}
            ];
            return [
                {
                    key: 'category',
                    label: 'Категория',
                    value: this.getCategoryLabel(this.form.category) || 'Не выбрана',
                    icon: 'fas fa-folder-open'
                },
                {
                    key: 'catalog',
                    label: 'Справочник',
                    value: this.getItemCatalogSummary(),
                    icon: 'fas fa-book'
                },
                {
                    key: 'participants',
                    label: 'Участники',
                    value: this.getParticipantsSectionSummary(),
                    icon: 'fas fa-users'
                },
                {
                    key: 'automation',
                    label: 'Автоматизация',
                    value: this.getAutomationSummary(),
                    icon: 'fas fa-bolt'
                }
            ];
        },
        configurationIssues() {
            const issues = [];
            const workflowConfig = this.form.workflow_config || {};
            const requiredAttachments = workflowConfig.requiredAttachments || {};
            const linkedRequests = workflowConfig.linkedRequests || {};
            const deadlinesConfig = workflowConfig.deadlinesConfig || {};

            if (!String(this.form.name || '').trim()) {
                issues.push({
                    key: 'name-required',
                    section: 'passport',
                    text: 'Не указано название типа заявки.'
                });
            }

            if (!this.form.category) {
                issues.push({
                    key: 'category-required',
                    section: 'passport',
                    text: 'Не выбрана категория типа заявки.'
                });
            }

            if (this.isSubmissionExtension) {
                const rules = this.form.approvers && this.form.approvers.participants || [];
                const users = new Set(rules.reduce((ids, rule) => ids.concat(rule.user_ids || []), []).map(Number).filter(id => id > 0));
                if (!rules.length || (rules.every(rule => rule.type === "specific_users") && !users.size)) issues.push({key: "approvers-empty", section: "participants", text: "Выберите хотя бы одного действующего согласующего."});
                return issues;
            }

            if (this.itemCatalogEnabled && !workflowConfig.itemCatalog.type) {
                issues.push({
                    key: 'catalog-type-required',
                    section: 'form',
                    text: 'Справочник позиций включен, но тип справочника не выбран.'
                });
            }

            if (requiredAttachments.enabled && this.getActiveRulesCount(requiredAttachments.rules) === 0) {
                issues.push({
                    key: 'required-attachments-empty',
                    section: 'form',
                    text: 'Обязательные документы включены, но ни одно правило документа не настроено.'
                });
            }

            if (linkedRequests.enabled && this.getActiveRulesCount(linkedRequests.rules) === 0) {
                issues.push({
                    key: 'linked-requests-empty',
                    section: 'automation',
                    text: 'Связанные заявки включены, но правила создания связанных заявок не добавлены.'
                });
            }

            if (this.form.executor_assignment_manual && this.getParticipantRulesCount(this.form.executor_assigner) === 0) {
                issues.push({
                    key: 'assigner-empty',
                    section: 'participants',
                    text: 'Исполнитель назначается вручную, но назначающий не настроен.'
                });
            }

            if (!this.isReportAnnulment && this.getParticipantRulesCount(this.form.executors) === 0) {
                issues.push({
                    key: 'executors-empty',
                    section: 'participants',
                    text: 'Пул исполнителей пустой. Заявку может быть некому взять в работу.'
                });
            }

            if (
                workflowConfig.buildingProcess &&
                workflowConfig.buildingProcess.enabled &&
                workflowConfig.buildingProcess.required !== true &&
                this.hasObjectRoleParticipant(this.form.approvers)
            ) {
                issues.push({
                    key: 'optional-object-role-approver',
                    section: 'participants',
                    text: 'Объект необязателен, но среди согласующих есть роль на объекте. Без выбранного объекта такие согласующие не назначатся.'
                });
            }

            if (this.form.late_approval_config &&
                this.form.late_approval_config.enabled &&
                this.getParticipantRulesCount(this.form.late_approval_config.approvers) === 0
            ) {
                issues.push({
                    key: 'late-approvers-empty',
                    section: 'deadlines',
                    text: 'Дополнительное согласование при нарушении сроков включено, но согласующие не выбраны.'
                });
            }

            if (
                deadlinesConfig.autoCloseAwaitingAcceptanceEnabled &&
                workflowConfig.requiresAcceptance === false
            ) {
                issues.push({
                    key: 'auto-close-without-acceptance',
                    section: 'automation',
                    text: 'Автозакрытие приемки включено, но приемка для workflow отключена.'
                });
            }

            return issues;
        },
        sectionIssueCounts() {
            return this.configurationIssues.reduce((acc, issue) => {
                acc[issue.section] = (acc[issue.section] || 0) + 1;
                return acc;
            }, {});
        },
        itemCatalogEnabled() {
            return Boolean(!this.isSubmissionExtension && this.form.workflow_config &&
                this.form.workflow_config.itemCatalog &&
                this.form.workflow_config.itemCatalog.enabled);
        },
        employeeCatalogEnabled() {
            return Boolean(this.itemCatalogEnabled &&
                this.form.workflow_config.itemCatalog.type === 'employees');
        },
        fuelCardCatalogEnabled() {
            return Boolean(this.itemCatalogEnabled &&
                this.form.workflow_config.itemCatalog.type === 'fuel_cards');
        },
        mechanicAssetCatalogEnabled() {
            return Boolean(this.itemCatalogEnabled &&
                this.form.workflow_config.itemCatalog.type === 'mechanic_assets');
        },
        linkedRequestTypeOptions() {
            return (this.requestTypesForLinks || [])
                .map(type => ({
                    value: type.id,
                    text: type.name
                }))
                .sort((a, b) => a.text.localeCompare(b.text));
        }
    },
    watch: {
        "form.category"(value, previous) {
            const keys = ["passport", "form", "participants", "deadlines", "notifications", "workflow", "automation", "customFields"];
            const oldKeys = extensionProfile.isExtension(previous) ? extensionProfile.SECTIONS : keys;
            const activeKey = oldKeys[this.activeSectionIndex];
            this.activeSectionIndex = Math.max(0, this.requestTypeSections.findIndex(section => section.key === activeKey));
            this.clearSearchHighlight();
        },
        settingsSearchQuery() {
            if (!this.normalizedSettingsSearchQuery || this.searchableRequestTypeSections.length === 0) {
                this.clearSearchHighlight();
                return;
            }

            const highlightedTargetKey = this.highlightedSearchTargetKey;
            const activeKey = this.activeRequestTypeSection && this.activeRequestTypeSection.key;
            const activeVisible = this.searchableRequestTypeSections.some(section => section.key === activeKey);
            if (!activeVisible) {
                this.setActiveSection(this.searchableRequestTypeSections[0].key);
                return;
            }

            if (highlightedTargetKey) {
                this.refreshSearchHighlight(highlightedTargetKey);
                return;
            }

            this.clearSearchHighlight();
            this.$nextTick(() => {
                this.renderActiveSectionSearchBubbles();
            });
        }
    },
    methods: {
        isSectionAvailable(key) { if (this.isReportAnnulment && key === 'automation') return false; return !this.isSubmissionExtension || extensionProfile.SECTIONS.includes(key); },
        setActiveSection(sectionKey, renderSearchBubbles = true) {
            const index = this.requestTypeSections.findIndex(section => section.key === sectionKey);
            if (index >= 0) {
                this.activeSectionIndex = index;
                if (renderSearchBubbles && this.normalizedSettingsSearchQuery) {
                    this.clearSearchHighlight();
                    this.$nextTick(() => {
                        this.renderActiveSectionSearchBubbles();
                    });
                }
            }
        },
        clearSettingsSearch() {
            this.settingsSearchQuery = '';
        },
        normalizeSettingsSearchText(value) {
            return String(value || '')
                .toLowerCase()
                .replace(/ё/g, 'е')
                .replace(/[_-]+/g, ' ')
                .replace(/\s+/g, ' ')
                .trim();
        },
        matchesSettingsSearch(value, tokens) {
            const normalized = this.normalizeSettingsSearchText(value);
            if (!normalized) {
                return false;
            }
            return tokens.every(token => normalized.indexOf(token) !== -1);
        },
        getUniqueSearchTerms(terms) {
            const seen = {};
            return (terms || [])
                .map(term => String(term || '').trim())
                .filter(term => {
                    if (!term) {
                        return false;
                    }
                    const key = this.normalizeSettingsSearchText(term);
                    if (seen[key]) {
                        return false;
                    }
                    seen[key] = true;
                    return true;
                });
        },
        getOptionSearchTerms(options) {
            return (options || [])
                .map(option => option && option.text)
                .filter(Boolean);
        },
        getSectionDefaultTargetKey(sectionKey) {
            const targets = {
                passport: 'passport-basics',
                form: 'form-description',
                participants: 'participants-initiator',
                deadlines: 'deadlines-regulatory',
                notifications: 'notifications-statuses',
                workflow: 'workflow-config',
                automation: 'automation-linked',
                customFields: 'custom-fields'
            };
            if (this.isSubmissionExtension && sectionKey === "form") return "form-files";
            if (this.isSubmissionExtension && sectionKey === "participants") return "participants-approvers";
            return targets[sectionKey] || sectionKey;
        },
        createSearchParameter(label, targetKey, aliases = []) {
            const normalizedLabel = this.normalizeSettingsSearchText(label).replace(/\s+/g, '-');
            return {
                key: `${targetKey}-${normalizedLabel}`,
                label,
                targetKey,
                aliases
            };
        },
        getSearchParameterText(result) {
            if (!result) {
                return '';
            }
            return [result.label].concat(result.aliases || []).join(' ');
        },
        getSearchResultsBubbleLabel(count) {
            return `Результатов: ${count}`;
        },
        getActiveSectionSearchResults() {
            const activeKey = this.activeRequestTypeSection && this.activeRequestTypeSection.key;
            const activeSection = this.searchableRequestTypeSections.find(section => section.key === activeKey);
            return activeSection && Array.isArray(activeSection.searchResults)
                ? activeSection.searchResults
                : [];
        },
        findSearchResultByTargetKey(targetKey) {
            if (!targetKey) {
                return null;
            }
            const result = this.getActiveSectionSearchResults().find(item => item.targetKey === targetKey);
            if (result) {
                return result;
            }
            return null;
        },
        createOptionSearchParameters(options, targetKey) {
            return (options || [])
                .filter(option => option && option.text)
                .map(option => this.createSearchParameter(option.text, targetKey, [option.value]));
        },
        getSectionSearchParameters(sectionKey) {
            const terms = {
                passport: [
                    this.createSearchParameter('Название типа заявки', 'passport-basics', ['name']),
                    this.createSearchParameter('Категория', 'passport-basics', ['category']),
                    this.createSearchParameter('Описание', 'passport-basics', ['description']),
                    this.createSearchParameter('Активность типа', 'passport-basics', ['Активен', 'Отключен']),
                    this.createSearchParameter('Название заявки', 'passport-basics', ['Требовать поле Название заявки']),
                    this.createSearchParameter('Внешний вид заявки', 'passport-appearance', ['Иконка блока', 'Цвет линии карточки']),
                    this.createSearchParameter('Иконка блока', 'passport-appearance', ['icon']),
                    this.createSearchParameter('Цвет линии карточки', 'passport-appearance', ['color', 'variant'])
                ],
                form: [
                    this.createSearchParameter('Поле описания в заявке', 'form-description', ['Показывать описание', 'Требовать описание']),
                    this.createSearchParameter('Строительный процесс', 'form-building-process', ['Объект', 'Обязательно для заполнения']),
                    this.createSearchParameter('Редактирование заявки', 'form-editing', ['Редактирование после согласования', 'Редактирование после взятия в работу']),
                    this.createSearchParameter('Обязательные документы', 'form-required-attachments', ['Документы по этапам', 'Быстрый просмотр документов']),
                    this.createSearchParameter('Справочник позиций', 'form-item-catalog', ['Материалы', 'Сотрудники', 'Топливные карты', 'Техника отдела механиков']),
                    this.createSearchParameter('Предпросмотр позиций', 'form-item-catalog', ['Кнопка позиций в списке заявок']),
                    this.createSearchParameter('Группы материалов', 'form-item-catalog', ['Запрещенные группы материалов']),
                    this.createSearchParameter('Сотрудники в заявке', 'form-item-catalog', ['Наблюдатели по сотруднику', 'Обходной лист']),
                    this.createSearchParameter('Файлы заявки', 'form-files', ['Вложения', 'Прикрепление файлов']),
                    this.createSearchParameter('Размер файлов', 'form-files', ['Максимальный размер файла']),
                    this.createSearchParameter('Количество файлов', 'form-files', ['Максимальное количество файлов']),
                    this.createSearchParameter('Типы файлов', 'form-files', ['Разрешенные типы файлов']),
                    this.createSearchParameter('Комментарии', 'form-comments', ['Комментирование заявки']),
                    this.createSearchParameter('Вложения к комментариям', 'form-comments', ['Файлы в комментариях'])
                ].concat(
                    this.createOptionSearchParameters(this.itemCatalogTypeOptions, 'form-item-catalog'),
                    this.createOptionSearchParameters(this.mechanicAssetCatalogScopeOptions, 'form-item-catalog'),
                    this.createOptionSearchParameters(this.fileTypeOptions, 'form-files'),
                    this.createOptionSearchParameters(this.commentPermissionOptions, 'form-comments')
                ),
                participants: [
                    this.createSearchParameter('Инициатор', 'participants-initiator', ['Создатель заявки']),
                    this.createSearchParameter('Согласующие', 'participants-approvers', ['Кто согласовывает заявки']),
                    this.createSearchParameter('Наблюдатели', 'participants-observers', ['Видят заявки', 'Могут комментировать']),
                    this.createSearchParameter('Исполнители', 'participants-executors', ['Кто выполняет заявки']),
                    this.createSearchParameter('Ручное назначение исполнителя', 'participants-executors', ['Назначающие', 'Ожидание назначения']),
                    this.createSearchParameter('Мультиисполнители', 'participants-multi-executor', ['Распределение строк', 'Самоназначение']),
                    this.createSearchParameter('Кто может назначать исполнителя', 'participants-assigner', ['Назначающий']),
                    this.createSearchParameter('Пул исполнителей', 'participants-executor-pool', ['Пул доступных исполнителей']),
                    this.createSearchParameter('Пользователи, отделы и роли на объекте', 'participants-initiator', ['Пользователи', 'Отдел', 'Роль на объекте', 'Все пользователи'])
                ],
                deadlines: [
                    this.createSearchParameter('Срок подачи заявки', 'deadlines-regulatory', ['Регламент подачи', 'Дней']),
                    this.createSearchParameter('Аварийная заявка', 'deadlines-regulatory', ['Без ограничений по срокам']),
                    this.createSearchParameter('Обоснование срочности', 'deadlines-regulatory', ['Нарушение сроков', 'Просрочка']),
                    this.createSearchParameter('Дополнительное согласование при нарушении сроков', 'deadlines-late-approval', ['Позднее согласование']),
                    this.createSearchParameter('Изменение срока выполнения', 'deadlines-editing', ['Кто может менять срок']),
                    this.createSearchParameter('До первого согласования', 'deadlines-editing', ['Редакторы срока до согласования']),
                    this.createSearchParameter('После первого согласования', 'deadlines-editing', ['Редакторы срока после согласования'])
                ],
                notifications: [
                    this.createSearchParameter('Уведомления по статусам', 'notifications-statuses', ['Статусы заявки', 'Telegram', 'Кому отправлять']),
                    this.createSearchParameter('Заявка без движения', 'notifications-stale', ['Напоминания', 'Без движения', 'stale']),
                    this.createSearchParameter('Excel в уведомлениях', 'notifications-excel', ['Excel-вложения', 'Вложения в уведомления']),
                    this.createSearchParameter('Выгрузка позиций заявки в Excel', 'notifications-excel', ['Экспорт Excel'])
                ],
                workflow: [
                    this.createSearchParameter('Маршрут заявки', 'workflow-config', ['Workflow', 'State machine']),
                    this.createSearchParameter('Статусы', 'workflow-config', ['Статусы заявки']),
                    this.createSearchParameter('Действия', 'workflow-config', ['Роли действий']),
                    this.createSearchParameter('Переходы', 'workflow-config', ['Смена статуса']),
                    this.createSearchParameter('Права', 'workflow-config', ['Кто может выполнять действия']),
                    this.createSearchParameter('Согласование', 'workflow-config', ['Согласовать']),
                    this.createSearchParameter('Исполнение', 'workflow-config', ['Взятие в работу']),
                    this.createSearchParameter('Приемка', 'workflow-config', ['Принять результат']),
                    this.createSearchParameter('Возврат, отклонение и отмена', 'workflow-config', ['Возврат', 'Отклонение', 'Отмена']),
                    this.createSearchParameter('Завершение заявки', 'workflow-config', ['Завершение выполнения'])
                ].concat(
                    this.createOptionSearchParameters(this.requiredAttachmentTargetOptions, 'workflow-config'),
                    this.createOptionSearchParameters(this.requiredAttachmentUploadRoleOptions, 'workflow-config')
                ),
                automation: [
                    this.createSearchParameter('Связанные заявки', 'automation-linked', ['Создать связанную заявку']),
                    this.createSearchParameter('Данные для черновика связанной заявки', 'automation-linked', ['Копировать объект', 'Копировать описание', 'Копировать файлы', 'Копировать позиции']),
                    this.createSearchParameter('Перенос команды между связанными заявками', 'automation-linked', ['Доступ между заявками', 'Наблюдатели без уведомлений', 'Команда основания', 'Команда созданной заявки']),
                    this.createSearchParameter('Из созданной заявки в основание', 'automation-linked', ['Перенести согласующих в основание', 'Роли созданной заявки']),
                    this.createSearchParameter('Из основания в созданную заявку', 'automation-linked', ['Перенести команду основания', 'Роли заявки-основания']),
                    this.createSearchParameter('Действие с топливной картой', 'automation-fuel-card', ['Топливные карты', 'Выдать карту', 'Суточные лимиты']),
                    this.createSearchParameter('Действие со справочником механиков', 'automation-mechanic-assets', ['Техника механиков', 'Перемещение техники', 'Ремонт техники', 'Аренда техники']),
                    this.createSearchParameter('Автозакрытие приемки', 'automation-auto-close', ['Приемка', 'Ожидание приемки', 'Закрывать автоматически'])
                ].concat(
                    this.createOptionSearchParameters(this.linkedRequestRoleOptions, 'automation-linked'),
                    this.createOptionSearchParameters(this.linkedRequestTeamRoleOptions, 'automation-linked'),
                    this.createOptionSearchParameters(this.linkedRequestStatusOptions, 'automation-linked'),
                    this.createOptionSearchParameters(this.fuelCardTriggerStatusOptions, 'automation-fuel-card'),
                    this.createOptionSearchParameters(this.fuelCardOperationOptions, 'automation-fuel-card'),
                    this.createOptionSearchParameters(this.mechanicAssetTriggerStatusOptions, 'automation-mechanic-assets'),
                    this.createOptionSearchParameters(this.mechanicAssetTableModeOptions, 'automation-mechanic-assets'),
                    this.createOptionSearchParameters(this.mechanicAssetOperationOptions, 'automation-mechanic-assets')
                ),
                customFields: [
                    this.createSearchParameter('Дополнительные поля', 'custom-fields', ['Доп. поля', 'Пользовательские поля', 'Custom fields']),
                    this.createSearchParameter('Название и ключ поля', 'custom-fields', ['Название поля', 'Ключ поля']),
                    this.createSearchParameter('Тип поля', 'custom-fields', ['Select', 'Дата', 'Период']),
                    this.createSearchParameter('Обязательность и видимость поля', 'custom-fields', ['Обязательное поле', 'Видимость поля']),
                    this.createSearchParameter('Условия отображения', 'custom-fields', ['Условная видимость']),
                    this.createSearchParameter('Список значений', 'custom-fields', ['Опции выбора']),
                    this.createSearchParameter('Порядок полей', 'custom-fields', ['Сортировка полей'])
                ]
            };

            if (this.isSubmissionExtension) {
                if (!this.isSectionAvailable(sectionKey)) return [];
                if (sectionKey === "form") return [
                    this.createSearchParameter("Файлы заявки", "form-files", ["Вложения", "Прикрепление файлов"]),
                    this.createSearchParameter("Комментарии", "form-comments", ["Комментирование заявки"])
                ];
                if (sectionKey === "notifications") return [
                    this.createSearchParameter("Создание и итоговое решение", "notifications-statuses", ["Отправлена", "Продление согласовано", "Отклонена", "Отозвана", "Инициатор", "Согласующие", "Наблюдатели"]),
                    this.createSearchParameter("Заявка без движения", "notifications-stale", ["Напоминание", "Часы"])
                ];
                return (terms[sectionKey] || []).filter(item => extensionProfile.TARGETS.includes(item.targetKey) && !["Название заявки", "Файлы обязательны"].includes(item.label));
            }
            return terms[sectionKey] || [];
        },
        openSearchResult(sectionKey, result) {
            this.setActiveSection(sectionKey, false);
            this.$nextTick(() => {
                this.scrollToSearchTarget(
                    result && result.targetKey ? result.targetKey : this.getSectionDefaultTargetKey(sectionKey),
                    result
                );
            });
        },
        getSearchTargetElement(targetKey) {
            const safeTargetKey = String(targetKey || '').replace(/"/g, '\\"');
            return safeTargetKey
                ? this.$el.querySelector(`[data-search-target="${safeTargetKey}"]`)
                : null;
        },
        isSearchElementVisible(element) {
            if (!element || typeof window === 'undefined') {
                return false;
            }

            const style = window.getComputedStyle(element);
            const rect = element.getBoundingClientRect();
            return style.display !== 'none' &&
                style.visibility !== 'hidden' &&
                rect.width > 0 &&
                rect.height > 0;
        },
        getVisibleSearchTargetElement(targetKey, sectionKey = null) {
            const target = this.getSearchTargetElement(targetKey);
            if (this.isSearchElementVisible(target)) {
                return target;
            }

            const defaultTargetKey = this.getSectionDefaultTargetKey(sectionKey || (this.activeRequestTypeSection && this.activeRequestTypeSection.key));
            const defaultTarget = this.getSearchTargetElement(defaultTargetKey);
            return this.isSearchElementVisible(defaultTarget) ? defaultTarget : null;
        },
        getActiveSectionSearchBubbleGroups() {
            const activeKey = this.activeRequestTypeSection && this.activeRequestTypeSection.key;
            const groups = {};
            this.getActiveSectionSearchResults().forEach(result => {
                const exactElement = this.getSearchTargetElement(result.targetKey);
                const exactElementVisible = this.isSearchElementVisible(exactElement);
                const hasVisibleMatch = exactElementVisible && this.hasSearchWordMatch(exactElement, result);
                if (hasVisibleMatch) {
                    return;
                }

                const element = exactElementVisible
                    ? exactElement
                    : this.getVisibleSearchTargetElement(result.targetKey, activeKey);
                if (!element) {
                    return;
                }

                const groupKey = element.getAttribute('data-search-target') || result.targetKey;
                if (!groups[groupKey]) {
                    groups[groupKey] = {
                        key: groupKey,
                        element,
                        count: 0,
                        targetKeys: []
                    };
                }
                groups[groupKey].count += 1;
                groups[groupKey].targetKeys.push(result.targetKey);
            });

            return Object.keys(groups).map(key => groups[key]);
        },
        clearSearchBubbles() {
            this.searchBubbleElements.forEach(bubble => {
                const parent = bubble && bubble.parentNode;
                if (parent) {
                    parent.classList.remove('request-type-search-target--has-bubble');
                    parent.removeChild(bubble);
                }
            });
            this.searchBubbleElements = [];
            this.unbindSearchBubbleMouseAvoidance();
        },
        renderActiveSectionSearchBubbles(focusedTargetKey = null) {
            this.clearSearchBubbles();
            if (!this.normalizedSettingsSearchQuery || typeof document === 'undefined') {
                return;
            }

            this.getActiveSectionSearchBubbleGroups().forEach(group => {
                const bubble = document.createElement('div');
                bubble.className = 'request-type-search-bubble search-bubble';
                if (focusedTargetKey && group.targetKeys.indexOf(focusedTargetKey) !== -1) {
                    bubble.className += ' request-type-search-bubble--active';
                }
                bubble.setAttribute('data-results', String(group.count));
                bubble.setAttribute('aria-hidden', 'true');

                const innards = document.createElement('div');
                innards.className = 'request-type-search-bubble__innards search-bubble-innards';
                innards.textContent = this.getSearchResultsBubbleLabel(group.count);
                bubble.appendChild(innards);

                group.element.classList.add('request-type-search-target--has-bubble');
                group.element.appendChild(bubble);
                this.searchBubbleElements.push(bubble);
            });
            this.bindSearchBubbleMouseAvoidance();
        },
        bindSearchBubbleMouseAvoidance() {
            if (this.searchBubbleMouseMoveHandler || !this.$el || !this.searchBubbleElements.length) {
                return;
            }

            this.searchBubbleMouseMoveHandler = event => {
                this.searchBubbleElements.forEach(bubble => {
                    if (!bubble || !bubble.parentNode) {
                        return;
                    }

                    const rect = bubble.getBoundingClientRect();
                    const nearBubble = event.clientX >= rect.left - 18 &&
                        event.clientX <= rect.right + 18 &&
                        event.clientY >= rect.top - 18 &&
                        event.clientY <= rect.bottom + 18;
                    bubble.classList.toggle('request-type-search-bubble--avoid', nearBubble);
                });
            };
            this.$el.addEventListener('mousemove', this.searchBubbleMouseMoveHandler);
        },
        unbindSearchBubbleMouseAvoidance() {
            if (this.searchBubbleMouseMoveHandler && this.$el) {
                this.$el.removeEventListener('mousemove', this.searchBubbleMouseMoveHandler);
            }
            this.searchBubbleMouseMoveHandler = null;
        },
        scrollToSearchTarget(targetKey, result = null) {
            const activeKey = this.activeRequestTypeSection && this.activeRequestTypeSection.key;
            const target = this.getVisibleSearchTargetElement(targetKey, activeKey);
            const element = target || this.$el.querySelector('.request-type-main');

            if (!element) {
                return;
            }

            this.applySearchHighlight(element, targetKey, result);

            if (typeof element.scrollIntoView === 'function') {
                element.scrollIntoView({
                    behavior: 'smooth',
                    block: 'center'
                });
            }
        },
        applySearchHighlight(element, targetKey, result = null) {
            this.clearSearchHighlight();
            this.highlightedSearchElement = element;
            this.highlightedSearchTargetKey = targetKey;
            this.highlightSearchWords(element, result);
            this.renderActiveSectionSearchBubbles(targetKey);
        },
        refreshSearchHighlight(targetKey) {
            const result = this.findSearchResultByTargetKey(targetKey);
            if (!result) {
                this.clearSearchHighlight();
                this.$nextTick(() => {
                    this.renderActiveSectionSearchBubbles();
                });
                return;
            }

            this.$nextTick(() => {
                const activeKey = this.activeRequestTypeSection && this.activeRequestTypeSection.key;
                const element = this.getVisibleSearchTargetElement(targetKey, activeKey);
                if (!element || !this.normalizedSettingsSearchQuery) {
                    this.clearSearchHighlight();
                    return;
                }

                this.applySearchHighlight(element, targetKey, result);
            });
        },
        highlightSearchWords(element, result = null) {
            let marksCount = 0;
            const tokenGroups = this.getSearchHighlightTokenGroups(result);
            for (let i = 0; i < tokenGroups.length && marksCount === 0; i += 1) {
                marksCount = this.wrapSearchWords(element, tokenGroups[i]);
            }

            return marksCount;
        },
        hasSearchWordMatch(element, result = null) {
            const tokenGroups = this.getSearchHighlightTokenGroups(result);
            return tokenGroups.some(tokens => this.countSearchWordMatches(element, tokens) > 0);
        },
        getSearchHighlightTokenGroups(result = null) {
            const tokenGroups = [];
            const queryTokens = this.settingsSearchTokens
                .filter(token => token.length >= 2);
            if (queryTokens.length) {
                tokenGroups.push(queryTokens);
            }
            if (result) {
                const fallbackTokens = this.getSearchHighlightFallbackTokens(result);
                if (fallbackTokens.length) {
                    tokenGroups.push(fallbackTokens);
                }
            }
            return tokenGroups;
        },
        getSearchHighlightFallbackTokens(result) {
            const stopWords = {
                'для': true,
                'или': true,
                'при': true,
                'без': true,
                'как': true,
                'что': true,
                'кто': true,
                'может': true,
                'можно': true,
                'тип': true,
                'типа': true,
                'заявка': true,
                'заявки': true,
                'заявок': true,
                'действие': true,
                'настройка': true,
                'настройки': true
            };
            return this.normalizeSettingsSearchText(this.getSearchParameterText(result))
                .split(/\s+/)
                .filter(token => token.length >= 4 && !stopWords[token])
                .slice(0, 8);
        },
        getNormalizedSearchTokens(tokens) {
            const normalizedTokens = this.getUniqueSearchTerms(tokens)
                .map(token => this.normalizeSettingsSearchText(token))
                .filter(Boolean);

            return normalizedTokens;
        },
        getSearchTextNodes(element) {
            if (!element || typeof document === 'undefined') {
                return [];
            }
            const ignoredTags = {
                INPUT: true,
                TEXTAREA: true,
                SELECT: true,
                OPTION: true,
                SCRIPT: true,
                STYLE: true,
                SVG: true
            };
            const textNodes = [];
            const walker = document.createTreeWalker(
                element,
                NodeFilter.SHOW_TEXT,
                {
                    acceptNode: node => {
                        const parent = node.parentNode;
                        if (!parent || ignoredTags[parent.nodeName]) {
                            return NodeFilter.FILTER_REJECT;
                        }
                        if (parent.closest && parent.closest('.search-bubble')) {
                            return NodeFilter.FILTER_REJECT;
                        }
                        if (!String(node.nodeValue || '').trim()) {
                            return NodeFilter.FILTER_REJECT;
                        }
                        return NodeFilter.FILTER_ACCEPT;
                    }
                }
            );
            let node = walker.nextNode();
            while (node) {
                textNodes.push(node);
                node = walker.nextNode();
            }
            return textNodes;
        },
        countSearchWordMatches(element, tokens) {
            const normalizedTokens = this.getNormalizedSearchTokens(tokens);
            if (!normalizedTokens.length || typeof document === 'undefined') {
                return 0;
            }

            const wordPattern = /([A-Za-zА-Яа-яЁё0-9_-]+)/g;
            let marksCount = 0;
            this.getSearchTextNodes(element).forEach(textNode => {
                const text = textNode.nodeValue || '';
                let match = wordPattern.exec(text);
                while (match) {
                    if (this.isSearchWordMatch(match[0], normalizedTokens)) {
                        marksCount += 1;
                    }
                    match = wordPattern.exec(text);
                }
            });
            return marksCount;
        },
        wrapSearchWords(element, tokens) {
            const normalizedTokens = this.getNormalizedSearchTokens(tokens);

            if (!normalizedTokens.length || typeof document === 'undefined') {
                return 0;
            }

            let marksCount = 0;
            this.getSearchTextNodes(element).forEach(textNode => {
                marksCount += this.wrapSearchWordsInTextNode(textNode, normalizedTokens);
            });
            return marksCount;
        },
        wrapSearchWordsInTextNode(textNode, tokens) {
            const text = textNode.nodeValue || '';
            const wordPattern = /([A-Za-zА-Яа-яЁё0-9_-]+)/g;
            let lastIndex = 0;
            let marksCount = 0;
            let match = wordPattern.exec(text);
            const fragment = document.createDocumentFragment();

            while (match) {
                const word = match[0];
                const start = match.index;
                const end = start + word.length;
                if (start > lastIndex) {
                    fragment.appendChild(document.createTextNode(text.slice(lastIndex, start)));
                }
                if (this.isSearchWordMatch(word, tokens)) {
                    const mark = document.createElement('mark');
                    mark.className = 'request-type-search-word-highlight';
                    mark.textContent = word;
                    fragment.appendChild(mark);
                    this.searchHighlightMarks.push(mark);
                    marksCount += 1;
                } else {
                    fragment.appendChild(document.createTextNode(word));
                }
                lastIndex = end;
                match = wordPattern.exec(text);
            }

            if (marksCount === 0) {
                return 0;
            }

            if (lastIndex < text.length) {
                fragment.appendChild(document.createTextNode(text.slice(lastIndex)));
            }
            textNode.parentNode.replaceChild(fragment, textNode);
            return marksCount;
        },
        isSearchWordMatch(word, tokens) {
            const normalizedWord = this.normalizeSettingsSearchText(word);
            return tokens.some(token => {
                if (normalizedWord.indexOf(token) !== -1) {
                    return true;
                }
                const stem = token.length >= 5 ? token.slice(0, -1) : token;
                return stem.length >= 4 && normalizedWord.indexOf(stem) !== -1;
            });
        },
        clearSearchHighlight() {
            this.clearSearchBubbles();
            this.searchHighlightMarks.forEach(mark => {
                const parent = mark.parentNode;
                if (!parent) {
                    return;
                }
                parent.replaceChild(document.createTextNode(mark.textContent || ''), mark);
                parent.normalize();
            });
            this.searchHighlightMarks = [];
            if (this.highlightedSearchElement) {
                this.highlightedSearchElement = null;
            }
            this.highlightedSearchTargetKey = null;
        },
        getSectionIssueCount(sectionKey) {
            return this.sectionIssueCounts[sectionKey] || 0;
        },
        getCategoryLabel(value) {
            const option = this.categoryOptions.find(item => item.value === value);
            return option ? option.text : '';
        },
        getOptionText(options, value, fallback = '') {
            const option = (options || []).find(item => item.value === value);
            return option ? option.text : fallback;
        },
        pluralizeRu(count, one, few, many) {
            const normalized = Math.abs(Number(count) || 0);
            const mod10 = normalized % 10;
            const mod100 = normalized % 100;
            if (mod10 === 1 && mod100 !== 11) {
                return one;
            }
            if (mod10 >= 2 && mod10 <= 4 && (mod100 < 12 || mod100 > 14)) {
                return few;
            }
            return many;
        },
        getActiveRulesCount(rules) {
            return (Array.isArray(rules) ? rules : [])
                .filter(rule => !rule || rule.active !== false)
                .length;
        },
        getParticipantRulesCount(config) {
            if (!config || typeof config !== 'object') {
                return 0;
            }
            if (Array.isArray(config.participants)) {
                return config.participants.length;
            }
            return config.type ? 1 : 0;
        },
        getTotalParticipantRulesCount() {
            if (this.isSubmissionExtension) return [this.form.approvers, this.form.observers].reduce((sum, config) => sum + this.getParticipantRulesCount(config), 0);
            return [
                this.form.initiator_config,
                this.form.approvers,
                this.form.observers,
                this.form.executors,
                this.form.executor_assignment_manual ? this.form.executor_assigner : null
            ].reduce((sum, config) => sum + this.getParticipantRulesCount(config), 0);
        },
        hasObjectRoleParticipant(config) {
            if (!config || typeof config !== 'object') {
                return false;
            }
            const participants = Array.isArray(config.participants)
                ? config.participants
                : (config.type ? [config] : []);
            return participants.some(participant => participant && participant.type === 'object_role');
        },
        getBasicsSectionSummary() {
            const category = this.getCategoryLabel(this.form.category);
            if (!category) {
                return 'Категория не выбрана';
            }
            return this.form.is_active ? category : `${category}, отключен`;
        },
        getFormSectionSummary() {
            if (this.isSubmissionExtension) return [this.form.allow_attachments ? "файлы разрешены" : "без файлов", this.form.allow_comments ? "комментарии разрешены" : "без комментариев"].join(", ");
            const parts = [];
            parts.push(this.itemCatalogEnabled ? this.getItemCatalogSummary() : 'без справочника');
            if (this.form.workflow_config.buildingProcess && this.form.workflow_config.buildingProcess.enabled) {
                parts.push(this.form.workflow_config.buildingProcess.required ? 'объект обязателен' : 'объект опционален');
            }
            if (this.form.workflow_config.requiredAttachments && this.form.workflow_config.requiredAttachments.enabled) {
                const count = this.getActiveRulesCount(this.form.workflow_config.requiredAttachments.rules);
                parts.push(`${count} ${this.pluralizeRu(count, 'документ', 'документа', 'документов')}`);
            }
            return parts.join(', ');
        },
        getParticipantsSectionSummary() {
            const count = this.getTotalParticipantRulesCount();
            return `${count} ${this.pluralizeRu(count, 'правило', 'правила', 'правил')}`;
        },
        getDeadlinesSectionSummary() {
            if (this.form.is_emergency) {
                return 'Аварийная заявка';
            }
            const days = Number(this.form.submission_deadline_days) || 0;
            return `${days} ${this.pluralizeRu(days, 'день', 'дня', 'дней')} на подачу`;
        },
        getNotificationsSectionSummary() {
            const enabled = [];
            const workflowConfig = this.form.workflow_config || {};
            if (this.hasEnabledStatusNotifications()) {
                enabled.push('статусы');
            }
            if (workflowConfig.deadlinesConfig && workflowConfig.deadlinesConfig.staleRequestReminderEnabled) {
                enabled.push('без движения');
            }
            if (!this.isSubmissionExtension && workflowConfig.excelExport && workflowConfig.excelExport.generateOnNotification) {
                enabled.push('Excel');
            }
            return enabled.length ? enabled.join(', ') : 'Базовые уведомления';
        },
        hasEnabledStatusNotifications() {
            const statusConfig = this.form.status_config || {};
            return Object.keys(statusConfig).filter(key => !this.isSubmissionExtension || extensionProfile.NOTIFICATION_STATUSES.includes(key)).some(key => {
                const config = statusConfig[key];
                return config && config.enabled !== false && config.send_notifications !== false;
            });
        },
        getWorkflowSectionSummary() {
            const workflowConfig = this.form.workflow_config || {};
            const actions = Array.isArray(workflowConfig.allowedActions)
                ? workflowConfig.allowedActions.length
                : 0;
            if (actions > 0) {
                return `${actions} ${this.pluralizeRu(actions, 'действие', 'действия', 'действий')}`;
            }
            return 'Правила по умолчанию';
        },
        getCustomFieldsSectionSummary() {
            const count = Array.isArray(this.form.custom_fields)
                ? this.form.custom_fields.length
                : 0;
            return `${count} ${this.pluralizeRu(count, 'поле', 'поля', 'полей')}`;
        },
        getItemCatalogSummary() {
            if (!this.itemCatalogEnabled) {
                return 'Не подключен';
            }
            const catalog = this.form.workflow_config.itemCatalog || {};
            const label = this.getOptionText(this.itemCatalogTypeOptions, catalog.type, 'Позиции');
            if (catalog.type === 'materials' && Array.isArray(catalog.material_group_ids) && catalog.material_group_ids.length) {
                return `${label}: ${catalog.material_group_ids.length} групп`;
            }
            return label;
        },
        getAutomationSummary() {
            const workflowConfig = this.form.workflow_config || {};
            const enabled = [];
            if (workflowConfig.linkedRequests && workflowConfig.linkedRequests.enabled) {
                enabled.push('связанные');
            }
            if (workflowConfig.fuelCardAction && workflowConfig.fuelCardAction.enabled) {
                enabled.push('топливо');
            }
            if (workflowConfig.mechanicAssetAction && workflowConfig.mechanicAssetAction.enabled) {
                enabled.push('техника');
            }
            if (workflowConfig.deadlinesConfig && workflowConfig.deadlinesConfig.autoCloseAwaitingAcceptanceEnabled) {
                enabled.push('автозакрытие');
            }
            return enabled.length ? enabled.join(', ') : 'Не включена';
        },
        getDefaultItemCatalogConfig() {
            const isMaterialsType = this.form.category === 'materials';
            const isFuelCardsType = this.form.category === 'fuelCards';
            const isMechanicsType = this.form.category === 'mechanics';
            if (isFuelCardsType) {
                return {
                    ppe: {enabled: false, material_group_ids: []},
                    enabled: true,
                    type: 'fuel_cards',
                    label: 'Топливные карты',
                    material_group_ids: [],
                    excluded_material_group_ids: [],
                    allow_custom_items: false,
                    scope: 'all'
                };
            }
            if (isMechanicsType) {
                return {
                    ppe: {enabled: false, material_group_ids: []},
                    enabled: true,
                    type: 'mechanic_assets',
                    label: 'Техника',
                    material_group_ids: [],
                    excluded_material_group_ids: [],
                    allow_custom_items: false,
                    scope: 'all'
                };
            }
            return {
                    ppe: {enabled: false, material_group_ids: []},
                enabled: isMaterialsType,
                type: 'materials',
                label: 'Материалы',
                material_group_ids: [],
                excluded_material_group_ids: [],
                allow_custom_items: false,
                scope: 'all'
            };
        },
        normalizeMechanicAssetCatalogScope(value) {
            const scope = String(value || '').trim();
            return this.mechanicAssetCatalogScopeOptions.some(option => option.value === scope)
                ? scope
                : 'all';
        },
        getDefaultMultiExecutorConfig() {
            return {
                assignerEnabled: true,
                selfAssignEnabled: true
            };
        },
        normalizeParticipantsConfig(config, fallback) {
            const defaultConfig = cloneJson(fallback || { participants: [], allow_any_department_employee: false });
            if (!config) {
                return defaultConfig;
            }

            const source = cloneJson(config, {});
            if (Array.isArray(source.participants)) {
                return {
                    participants: source.participants,
                    allow_any_department_employee: source.allow_any_department_employee === true
                };
            }

            if (source.type) {
                return {
                    participants: [source],
                    allow_any_department_employee: false
                };
            }

            return defaultConfig;
        },
        normalizeDeadlineEditingConfig(config) {
            const defaults = getDefaultDeadlineEditingConfig();
            const source = config && typeof config === 'object'
                ? config
                : {};
            const beforeApprovalSource = source.beforeApproval ||
                source.before_approval ||
                source.editorsBeforeApproval ||
                source.editors_before_approval ||
                source.editors ||
                source.editorParticipants ||
                source.editor_participants ||
                source;
            const afterApprovalSource = source.afterApproval ||
                source.after_approval ||
                source.editorsAfterApproval ||
                source.editors_after_approval ||
                source.afterApprovalEditors ||
                source.after_approval_editors;

            return {
                beforeApproval: this.normalizeParticipantsConfig(
                    beforeApprovalSource,
                    defaults.beforeApproval
                ),
                afterApproval: this.normalizeParticipantsConfig(
                    afterApprovalSource,
                    defaults.afterApproval
                )
            };
        },
        getDefaultExcelExportConfig() {
            return {
                enabled: true,
                generateOnNotification: false
            };
        },
        getDefaultFuelCardActionConfig() {
            return {
                enabled: false,
                triggerStatus: 'completed',
                operation: 'assign_to_initiator'
            };
        },
        getDefaultMechanicAssetActionConfig() {
            return {
                enabled: false,
                triggerStatus: 'completed',
                operation: 'move_to_object',
                markInTransferOnShipment: false,
                requireMeterValue: false,
                requireCondition: false,
                _uid: `${Date.now()}_${Math.random()}`
            };
        },
        normalizeMechanicAssetActionConfig(source, enabledFallback = false) {
            const action = source || {};
            const defaultAction = this.getDefaultMechanicAssetActionConfig();
            return {
                enabled: action.enabled === true || enabledFallback === true,
                triggerStatus: action.triggerStatus || action.trigger_status || defaultAction.triggerStatus,
                operation: action.operation || defaultAction.operation,
                markInTransferOnShipment: action.markInTransferOnShipment === true ||
                    action.mark_in_transfer_on_shipment === true,
                requireMeterValue: action.requireMeterValue === true ||
                    action.require_meter_value === true,
                requireCondition: action.requireCondition === true ||
                    action.require_condition === true,
                _uid: action._uid || `${Date.now()}_${Math.random()}`
            };
        },
        ensureMechanicAssetActions() {
            if (!Array.isArray(this.form.workflow_config.mechanicAssetActions)) {
                this.$set(this.form.workflow_config, 'mechanicAssetActions', []);
            }
            if (this.form.workflow_config.mechanicAssetActions.length === 0) {
                this.form.workflow_config.mechanicAssetActions.push(
                    this.normalizeMechanicAssetActionConfig(
                        this.form.workflow_config.mechanicAssetAction,
                        this.form.workflow_config.mechanicAssetAction &&
                            this.form.workflow_config.mechanicAssetAction.enabled === true
                    )
                );
            }
        },
        syncPrimaryMechanicAssetAction() {
            this.ensureMechanicAssetActions();
            const enabled = this.form.workflow_config.mechanicAssetAction &&
                this.form.workflow_config.mechanicAssetAction.enabled === true;
            const firstAction = this.form.workflow_config.mechanicAssetActions[0] ||
                this.getDefaultMechanicAssetActionConfig();
            this.$set(this.form.workflow_config, 'mechanicAssetAction', Object.assign(
                {},
                firstAction,
                { enabled }
            ));
            this.$set(this.form.workflow_config, 'mechanicAssetActions',
                this.form.workflow_config.mechanicAssetActions.map(action => Object.assign(
                    {},
                    action,
                    { enabled }
                ))
            );
        },
        onMechanicAssetActionEnabledChange(value) {
            this.ensureMechanicAssetActions();
            this.$set(this.form.workflow_config.mechanicAssetAction, 'enabled', value === true);
            this.syncPrimaryMechanicAssetAction();
        },
        addMechanicAssetAction() {
            this.ensureMechanicAssetActions();
            const next = this.normalizeMechanicAssetActionConfig(
                { enabled: this.form.workflow_config.mechanicAssetAction.enabled === true },
                this.form.workflow_config.mechanicAssetAction.enabled === true
            );
            this.form.workflow_config.mechanicAssetActions.push(next);
            this.syncPrimaryMechanicAssetAction();
        },
        removeMechanicAssetAction(index) {
            this.ensureMechanicAssetActions();
            if (this.form.workflow_config.mechanicAssetActions.length <= 1) {
                return;
            }
            this.form.workflow_config.mechanicAssetActions.splice(index, 1);
            this.syncPrimaryMechanicAssetAction();
        },
        getDefaultLinkedRequestsConfig() {
            return {
                enabled: false,
                rules: []
            };
        },
        getDefaultRequiredAttachmentsConfig() {
            return {
                enabled: false,
                listPreview: {
                    enabled: false
                },
                rules: []
            };
        },
        createLinkedRequestRule() {
            return {
                _uid: `${Date.now()}_${Math.random()}`,
                key: `linked_${Date.now()}`,
                active: true,
                label: 'Создать связанную',
                targetRequestTypeId: null,
                allowedRoles: ['assigner', 'executor'],
                allowedStatuses: [],
                sourceLabel: 'Связанная заявка',
                targetLabel: 'Основание',
                titleTemplate: '{targetType} по заявке {sourceNumber}',
                copyBuildingProcess: true,
                copyDescription: false,
                copyFiles: false,
                copyItems: false,
                transferTargetTeamRolesToSourceObservers: [],
                transferSourceTeamRolesToTargetObservers: []
            };
        },
        createRequiredAttachmentRule() {
            return {
                _uid: `${Date.now()}_${Math.random()}`,
                key: '',
                active: true,
                label: '',
                requiredFor: ['create'],
                minCount: 1,
                uploadRoles: [],
                activationMode: 'always',
                initiatorChoice: false
            };
        },
        normalizeLinkedRequestRule(rule, index) {
            const source = rule && typeof rule === 'object' ? rule : {};
            const targetRequestTypeId = source.targetRequestTypeId ||
                source.target_request_type_id ||
                source.request_type_id ||
                null;
            const key = source.key || source.ruleKey || source.rule_key || `linked_${index + 1}`;
            return {
                _uid: source._uid || `${Date.now()}_${index}_${Math.random()}`,
                key,
                active: source.active !== false && source.enabled !== false,
                label: source.label || source.buttonLabel || source.button_label || 'Создать связанную',
                targetRequestTypeId,
                allowedRoles: Array.isArray(source.allowedRoles)
                    ? [...source.allowedRoles]
                    : (Array.isArray(source.allowed_roles) ? [...source.allowed_roles] : ['assigner', 'executor']),
                allowedStatuses: Array.isArray(source.allowedStatuses)
                    ? [...source.allowedStatuses]
                    : (Array.isArray(source.allowed_statuses) ? [...source.allowed_statuses] : []),
                sourceLabel: source.sourceLabel || source.source_label || 'Связанная заявка',
                targetLabel: source.targetLabel || source.target_label || 'Основание',
                titleTemplate: source.titleTemplate || source.title_template || '{targetType} по заявке {sourceNumber}',
                copyBuildingProcess: source.copyBuildingProcess !== false && source.copy_building_process !== false,
                copyDescription: source.copyDescription === true || source.copy_description === true,
                copyFiles: source.copyFiles === true || source.copy_files === true,
                copyItems: source.copyItems === true || source.copy_items === true,
                transferTargetTeamRolesToSourceObservers: this.normalizeLinkedRequestTransferRoles(
                    source.transferTargetTeamRolesToSourceObservers ||
                    source.transfer_target_team_roles_to_source_observers ||
                    source.targetTeamRolesToSourceObservers ||
                    source.target_team_roles_to_source_observers ||
                    (
                        source.addTargetApproversToSourceObservers === true ||
                        source.add_target_approvers_to_source_observers === true ||
                        source.syncApproversToSourceObservers === true ||
                        source.sync_approvers_to_source_observers === true
                            ? ['approver']
                            : []
                    )
                ),
                transferSourceTeamRolesToTargetObservers: this.normalizeLinkedRequestTransferRoles(
                    source.transferSourceTeamRolesToTargetObservers ||
                    source.transfer_source_team_roles_to_target_observers ||
                    source.sourceTeamRolesToTargetObservers ||
                    source.source_team_roles_to_target_observers ||
                    (
                        source.addSourceTeamToTargetObservers === true ||
                        source.add_source_team_to_target_observers === true ||
                        source.syncSourceTeamToTargetObservers === true ||
                        source.sync_source_team_to_target_observers === true
                            ? ['initiator', 'approver', 'assigner', 'executor', 'observer']
                            : []
                    )
                )
            };
        },
        normalizeLinkedRequestTransferRoles(roles) {
            const allowedRoles = this.linkedRequestTeamRoleOptions.map(option => option.value);
            return Array.isArray(roles)
                ? roles
                    .map(role => String(role || '').trim())
                    .filter(role => allowedRoles.indexOf(role) !== -1)
                    .filter((role, index, list) => list.indexOf(role) === index)
                : [];
        },
        normalizeRequiredAttachmentRule(rule, index) {
            return normalizeRequiredAttachmentRuleConfig(rule, index);
        },
        ensureItemCatalogConfig() {
            if (!this.form.workflow_config || typeof this.form.workflow_config !== 'object') {
                this.$set(this.form, 'workflow_config', {});
            }

            const oldItemCatalog = this.form.workflow_config.itemCatalog;
            const legacyItemCatalogRequiresBuildingProcess = oldItemCatalog &&
                oldItemCatalog.requires_building_process !== undefined
                ? Boolean(oldItemCatalog.requires_building_process)
                : undefined;
            const topLevelRequiresBuildingProcess = this.form.workflow_config.requiresBuildingProcess !== undefined
                ? this.form.workflow_config.requiresBuildingProcess
                : this.form.workflow_config.requires_building_process;
            const legacyRequiresBuildingProcess = topLevelRequiresBuildingProcess !== undefined
                ? Boolean(topLevelRequiresBuildingProcess)
                : (legacyItemCatalogRequiresBuildingProcess !== undefined
                    ? legacyItemCatalogRequiresBuildingProcess
                    : this.form.category === 'materials');
            const buildingProcessSource = this.form.workflow_config.buildingProcess ||
                this.form.workflow_config.building_process ||
                {};
            const hasBuildingProcessConfig = buildingProcessSource &&
                typeof buildingProcessSource === 'object' &&
                !Array.isArray(buildingProcessSource) &&
                (
                    buildingProcessSource.enabled !== undefined ||
                    buildingProcessSource.required !== undefined ||
                    buildingProcessSource.show !== undefined ||
                    buildingProcessSource.isRequired !== undefined
                );
            const buildingProcessRequired = hasBuildingProcessConfig
                ? (
                    buildingProcessSource.required === true ||
                    buildingProcessSource.isRequired === true ||
                    buildingProcessSource.required_for_create === true
                )
                : legacyRequiresBuildingProcess;
            const buildingProcessEnabled = hasBuildingProcessConfig
                ? (
                    buildingProcessSource.enabled === true ||
                    buildingProcessSource.show === true ||
                    buildingProcessRequired
                )
                : legacyRequiresBuildingProcess;
            this.$set(this.form.workflow_config, 'buildingProcess', {
                enabled: buildingProcessEnabled || buildingProcessRequired,
                required: buildingProcessEnabled ? buildingProcessRequired : false
            });
            this.$set(this.form.workflow_config, 'requiresBuildingProcess', buildingProcessRequired);
            this.$delete(this.form.workflow_config, 'building_process');
            this.$delete(this.form.workflow_config, 'requires_building_process');

            const hasExcelExportConfig = this.form.workflow_config.excelExport !== undefined ||
                this.form.workflow_config.excel_export !== undefined;
            const excelExportSource = this.form.workflow_config.excelExport ||
                this.form.workflow_config.excel_export ||
                {};
            const features = this.form.workflow_config.features || {};
            const excelEnabled = excelExportSource.enabled !== false &&
                excelExportSource.enabled !== 'false' &&
                excelExportSource.disabled !== true;
            const legacyGenerateOnNotification = !hasExcelExportConfig && (
                this.form.workflow_config.generateExcelOnNotification === true ||
                this.form.workflow_config.generate_excel_on_notification === true ||
                features.generateExcelOnNotification === true ||
                features.generate_excel_on_notification === true
            );
            const generateOnNotification = excelEnabled && (
                excelExportSource.generateOnNotification === true ||
                excelExportSource.generate_on_notification === true ||
                legacyGenerateOnNotification
            );
            this.$set(this.form.workflow_config, 'excelExport', {
                enabled: excelEnabled,
                generateOnNotification
            });
            this.$delete(this.form.workflow_config, 'excel_export');
            this.$delete(this.form.workflow_config, 'generateExcelOnNotification');
            this.$delete(this.form.workflow_config, 'generate_excel_on_notification');

            const employeeCatalogSubject = this.form.workflow_config.employeeCatalogSubject ||
                this.form.workflow_config.employee_catalog_subject ||
                {};
            this.$set(this.form.workflow_config, 'employeeCatalogSubject', {
                enabled: employeeCatalogSubject.enabled === true ||
                    employeeCatalogSubject.autoAddAsObserver === true ||
                    employeeCatalogSubject.auto_add_as_observer === true
            });
            this.$delete(this.form.workflow_config, 'employee_catalog_subject');

            const fuelCardAction = this.form.workflow_config.fuelCardAction ||
                this.form.workflow_config.fuel_card_action ||
                {};
            const defaultFuelCardAction = this.getDefaultFuelCardActionConfig();
            const triggerStatus = fuelCardAction.triggerStatus || fuelCardAction.trigger_status || defaultFuelCardAction.triggerStatus;
            const operation = fuelCardAction.operation || defaultFuelCardAction.operation;
            this.$set(this.form.workflow_config, 'fuelCardAction', {
                enabled: fuelCardAction.enabled === true,
                triggerStatus,
                operation
            });
            this.$delete(this.form.workflow_config, 'fuel_card_action');

            const legacyMechanicAssetAction = this.form.workflow_config.mechanicAssetAction ||
                this.form.workflow_config.mechanic_asset_action ||
                {};
            const mechanicAssetActionsSource = Array.isArray(
                this.form.workflow_config.mechanicAssetActions ||
                this.form.workflow_config.mechanic_asset_actions
            )
                ? (this.form.workflow_config.mechanicAssetActions ||
                    this.form.workflow_config.mechanic_asset_actions)
                : [];
            const mechanicEnabled = legacyMechanicAssetAction.enabled === true ||
                mechanicAssetActionsSource.some(action => action && action.enabled === true);
            const mechanicAssetActions = (mechanicAssetActionsSource.length
                ? mechanicAssetActionsSource
                : [legacyMechanicAssetAction])
                .map(action => this.normalizeMechanicAssetActionConfig(action, mechanicEnabled));
            this.$set(this.form.workflow_config, 'mechanicAssetActions', mechanicAssetActions);
            this.$set(this.form.workflow_config, 'mechanicAssetAction', Object.assign(
                {},
                mechanicAssetActions[0] || this.getDefaultMechanicAssetActionConfig(),
                { enabled: mechanicEnabled }
            ));
            this.$delete(this.form.workflow_config, 'mechanic_asset_action');
            this.$delete(this.form.workflow_config, 'mechanic_asset_actions');
            const mechanicTableMode = this.form.workflow_config.mechanicAssetTableMode ||
                this.form.workflow_config.mechanic_asset_table_mode ||
                'movement';
            this.$set(
                this.form.workflow_config,
                'mechanicAssetTableMode',
                this.mechanicAssetTableModeOptions.some(option => option.value === mechanicTableMode)
                    ? mechanicTableMode
                    : 'movement'
            );
            this.$delete(this.form.workflow_config, 'mechanic_asset_table_mode');

            const listItemsPreview = this.form.workflow_config.listItemsPreview ||
                this.form.workflow_config.list_items_preview ||
                {};
            this.$set(this.form.workflow_config, 'listItemsPreview', {
                enabled: listItemsPreview.enabled === true ||
                    this.form.workflow_config.showItemsInList === true ||
                    this.form.workflow_config.show_items_in_list === true
            });
            this.$delete(this.form.workflow_config, 'list_items_preview');
            this.$delete(this.form.workflow_config, 'showItemsInList');
            this.$delete(this.form.workflow_config, 'show_items_in_list');

            const requestEditing = this.form.workflow_config.requestEditing ||
                this.form.workflow_config.request_editing ||
                {};
            const requestEditingConfig = {
                allowAfterApproval: requestEditing.allowAfterApproval === true ||
                    requestEditing.allow_after_approval === true,
                allowAfterExecutionStartWithoutApprovers:
                    requestEditing.allowAfterExecutionStartWithoutApprovers === true ||
                    requestEditing.allow_after_execution_start_without_approvers === true ||
                    requestEditing.allowAfterWorkStartWithoutApprovers === true ||
                    requestEditing.allow_after_work_start_without_approvers === true
            };
            const editorRoles = Array.isArray(requestEditing.editorRoles)
                ? requestEditing.editorRoles
                : (Array.isArray(requestEditing.editor_roles) ? requestEditing.editor_roles : null);
            if (editorRoles) {
                requestEditingConfig.editorRoles = editorRoles
                    .map(role => String(role || '').trim())
                    .filter(Boolean);
            }
            this.$set(this.form.workflow_config, 'requestEditing', requestEditingConfig);
            this.$delete(this.form.workflow_config, 'request_editing');

            this.$set(this.form.workflow_config, 'fulfillment', {
                shippingRequired: (this.form.workflow_config.fulfillment || {}).shippingRequired === true,
                acceptanceRequired: (this.form.workflow_config.fulfillment || {}).acceptanceRequired === true
            });
            const closingCommentSource = this.form.workflow_config.closingComment ||
                this.form.workflow_config.closing_comment ||
                {};
            const closingCommentRequiredActionsSource = closingCommentSource.requiredActions ||
                closingCommentSource.required_actions;
            this.$set(this.form.workflow_config, 'closingComment', {
                required: closingCommentSource.required === true ||
                    closingCommentSource.isRequired === true ||
                    closingCommentSource.is_required === true ||
                    this.form.workflow_config.closingCommentRequired === true ||
                    this.form.workflow_config.closing_comment_required === true,
                requiredActions: Array.isArray(closingCommentRequiredActionsSource)
                    ? closingCommentRequiredActionsSource
                        .map(action => String(action || '').trim())
                        .filter(action => DEFAULT_CONFIGURABLE_COMMENT_ACTIONS.indexOf(action) !== -1)
                    : [...DEFAULT_CONFIGURABLE_COMMENT_ACTIONS]
            });
            this.$delete(this.form.workflow_config, 'closing_comment');
            this.$delete(this.form.workflow_config, 'closingCommentRequired');
            this.$delete(this.form.workflow_config, 'closing_comment_required');

            const deadlineEditing = this.form.workflow_config.deadlineEditing ||
                this.form.workflow_config.deadline_editing ||
                {};
            this.$set(
                this.form.workflow_config,
                'deadlineEditing',
                this.normalizeDeadlineEditingConfig(deadlineEditing)
            );
            this.$delete(this.form.workflow_config, 'deadline_editing');

            const deadlinesConfigSource = this.form.workflow_config.deadlinesConfig ||
                this.form.workflow_config.deadlines_config ||
                {};
            const staleCheckHours = Number(
                deadlinesConfigSource.staleCheckHours ||
                deadlinesConfigSource.stale_check_hours ||
                24
            );
            const autoCloseDays = Number(
                deadlinesConfigSource.autoCloseAwaitingAcceptanceDays ||
                deadlinesConfigSource.auto_close_awaiting_acceptance_days ||
                3
            );
            this.$set(this.form.workflow_config, 'deadlinesConfig', {
                staleRequestReminderEnabled: deadlinesConfigSource.staleRequestReminderEnabled === true ||
                    deadlinesConfigSource.stale_request_reminder_enabled === true ||
                    (
                        deadlinesConfigSource.staleRequestReminderEnabled === undefined &&
                        deadlinesConfigSource.stale_request_reminder_enabled === undefined &&
                        staleCheckHours > 0 &&
                        deadlinesConfigSource.staleCheckHours !== undefined
                    ),
                staleCheckHours: staleCheckHours > 0 ? staleCheckHours : 24,
                autoCloseAwaitingAcceptanceEnabled: deadlinesConfigSource.autoCloseAwaitingAcceptanceEnabled === true ||
                    deadlinesConfigSource.auto_close_awaiting_acceptance_enabled === true,
                autoCloseAwaitingAcceptanceDays: autoCloseDays > 0 ? autoCloseDays : 3
            });
            this.$delete(this.form.workflow_config, 'deadlines_config');

            this.$set(
                this.form.workflow_config,
                'cardBorderVariant',
                normalizeCardBorderVariant(
                    this.form.workflow_config.cardBorderVariant,
                    this.form.workflow_config.cardBorderColor
                )
            );
            this.$delete(this.form.workflow_config, 'cardBorderColor');
            this.$set(
                this.form.workflow_config,
                'requestTypeIcon',
                normalizeRequestTypeIcon(this.form.workflow_config.requestTypeIcon)
            );

            const multiExecutor = this.form.workflow_config.multiExecutor ||
                this.form.workflow_config.multi_executor ||
                {};
            this.$set(this.form.workflow_config, 'multiExecutor', {
                ...this.getDefaultMultiExecutorConfig(),
                assignerEnabled: multiExecutor.assignerEnabled !== false &&
                    multiExecutor.assigner_enabled !== false,
                selfAssignEnabled: multiExecutor.selfAssignEnabled !== false &&
                    multiExecutor.self_assign_enabled !== false
            });
            this.$delete(this.form.workflow_config, 'multi_executor');

            if (!this.form.workflow_config.descriptionField || typeof this.form.workflow_config.descriptionField !== 'object') {
                this.$set(this.form.workflow_config, 'descriptionField', {
                    enabled: true,
                    required: false
                });
            } else {
                this.$set(this.form.workflow_config, 'descriptionField', {
                    enabled: this.form.workflow_config.descriptionField.enabled !== false,
                    required: Boolean(this.form.workflow_config.descriptionField.required)
                });
            }

            const linkedRequestsSource = this.form.workflow_config.linkedRequests ||
                this.form.workflow_config.linked_requests ||
                this.getDefaultLinkedRequestsConfig();
            const linkedRules = Array.isArray(linkedRequestsSource.rules)
                ? linkedRequestsSource.rules
                : [];
            this.$set(this.form.workflow_config, 'linkedRequests', {
                enabled: linkedRequestsSource.enabled === true,
                rules: linkedRules.map((rule, index) => this.normalizeLinkedRequestRule(rule, index))
            });
            this.$delete(this.form.workflow_config, 'linked_requests');

            const requiredAttachmentsSource = this.form.workflow_config.requiredAttachments ||
                this.form.workflow_config.required_attachments ||
                this.getDefaultRequiredAttachmentsConfig();
            const requiredAttachmentsListPreview = requiredAttachmentsSource.listPreview ||
                requiredAttachmentsSource.list_preview ||
                {};
            const requiredRules = Array.isArray(requiredAttachmentsSource.rules)
                ? requiredAttachmentsSource.rules
                : [];
            this.$set(this.form.workflow_config, 'requiredAttachments', {
                enabled: requiredAttachmentsSource.enabled === true,
                listPreview: {
                    enabled: requiredAttachmentsListPreview.enabled === true ||
                        requiredAttachmentsSource.showInList === true ||
                        requiredAttachmentsSource.show_in_list === true
                },
                rules: requiredRules.map((rule, index) => this.normalizeRequiredAttachmentRule(rule, index))
            });
            this.$delete(this.form.workflow_config, 'required_attachments');

            if (!this.form.workflow_config.itemCatalog || typeof this.form.workflow_config.itemCatalog !== 'object') {
                this.$set(this.form.workflow_config, 'itemCatalog', this.getDefaultItemCatalogConfig());
            } else {
                const defaults = this.getDefaultItemCatalogConfig();
                const itemCatalogSource = this.form.workflow_config.itemCatalog;
                const itemCatalogType = itemCatalogSource.type || defaults.type;
                const itemCatalogScope = itemCatalogType === 'mechanic_assets'
                    ? this.normalizeMechanicAssetCatalogScope(
                        itemCatalogSource.scope ||
                        itemCatalogSource.catalog_scope ||
                        itemCatalogSource.catalogScope ||
                        itemCatalogSource.visibility ||
                        itemCatalogSource.mechanic_assets_scope ||
                        itemCatalogSource.mechanicAssetsScope
                    )
                    : 'all';
                this.$set(this.form.workflow_config, 'itemCatalog', {
                    ...defaults,
                    ...itemCatalogSource,
                    ppe: {enabled: Boolean(itemCatalogSource.ppe && itemCatalogSource.ppe.enabled),
                        material_group_ids: itemCatalogSource.ppe && Array.isArray(itemCatalogSource.ppe.material_group_ids) ? itemCatalogSource.ppe.material_group_ids : []},
                    type: itemCatalogType,
                    material_group_ids: Array.isArray(itemCatalogSource.material_group_ids)
                        ? itemCatalogSource.material_group_ids
                        : [],
                    excluded_material_group_ids: Array.isArray(itemCatalogSource.excluded_material_group_ids)
                        ? itemCatalogSource.excluded_material_group_ids
                        : [],
                    allow_custom_items: itemCatalogSource.allow_custom_items === true ||
                        itemCatalogSource.allowCustomItems === true ||
                        itemCatalogSource.allow_manual_items === true ||
                        itemCatalogSource.allowManualItems === true,
                    scope: itemCatalogScope
                });
            }
            this.$delete(this.form.workflow_config.itemCatalog, 'requires_building_process');
            this.$delete(this.form.workflow_config.itemCatalog, 'allowCustomItems');
            this.$delete(this.form.workflow_config.itemCatalog, 'allow_manual_items');
            this.$delete(this.form.workflow_config.itemCatalog, 'allowManualItems');
            this.$delete(this.form.workflow_config.itemCatalog, 'catalog_scope');
            this.$delete(this.form.workflow_config.itemCatalog, 'catalogScope');
            this.$delete(this.form.workflow_config.itemCatalog, 'visibility');
            this.$delete(this.form.workflow_config.itemCatalog, 'mechanic_assets_scope');
            this.$delete(this.form.workflow_config.itemCatalog, 'mechanicAssetsScope');
        },
        setCardBorderVariant(variant) {
            this.ensureItemCatalogConfig();
            this.$set(this.form.workflow_config, 'cardBorderVariant', normalizeCardBorderVariant(variant));
        },
        setRequestTypeIcon(icon) {
            this.ensureItemCatalogConfig();
            this.$set(this.form.workflow_config, 'requestTypeIcon', normalizeRequestTypeIcon(icon));
        },
        setItemCatalogEnabled(value) {
            this.ensureItemCatalogConfig();
            const enabled = Boolean(value);
            this.$set(this.form.workflow_config.itemCatalog, 'enabled', enabled);
            if (!enabled) {
                this.$set(this.form.workflow_config.listItemsPreview, 'enabled', false);
            }
        },
        addLinkedRequestRule() {
            this.ensureItemCatalogConfig();
            this.form.workflow_config.linkedRequests.rules.push(this.createLinkedRequestRule());
        },
        removeLinkedRequestRule(index) {
            this.form.workflow_config.linkedRequests.rules.splice(index, 1);
        },
        addRequiredAttachmentRule() {
            this.ensureItemCatalogConfig();
            this.form.workflow_config.requiredAttachments.rules.push(this.createRequiredAttachmentRule());
        },
        removeRequiredAttachmentRule(index) {
            this.form.workflow_config.requiredAttachments.rules.splice(index, 1);
        },
        onBuildingProcessEnabledChange(value) {
            this.ensureItemCatalogConfig();
            this.$set(this.form.workflow_config.buildingProcess, 'enabled', value === true);
            if (!value) {
                this.$set(this.form.workflow_config.buildingProcess, 'required', false);
            }
            this.$set(
                this.form.workflow_config,
                'requiresBuildingProcess',
                this.form.workflow_config.buildingProcess.required === true
            );
        },
        onBuildingProcessRequiredChange(value) {
            this.ensureItemCatalogConfig();
            this.$set(this.form.workflow_config.buildingProcess, 'required', value === true);
            if (value) {
                this.$set(this.form.workflow_config.buildingProcess, 'enabled', true);
            }
            this.$set(this.form.workflow_config, 'requiresBuildingProcess', value === true);
        },
        onCategoryChange(value) {
            this.ensureItemCatalogConfig();
            const catalog = this.form.workflow_config.itemCatalog;

            if (value === 'materials' && !catalog.enabled) {
                this.$set(catalog, 'enabled', true);
                this.$set(catalog, 'type', 'materials');
                this.$set(catalog, 'label', 'Материалы');
                this.$set(catalog, 'allow_custom_items', false);
                this.$set(catalog, 'scope', 'all');
                this.$set(this.form.workflow_config, 'buildingProcess', {
                    enabled: true,
                    required: true
                });
                this.$set(this.form.workflow_config, 'requiresBuildingProcess', true);
            } else if (value === 'fuelCards') {
                this.$set(catalog, 'enabled', true);
                this.$set(catalog, 'type', 'fuel_cards');
                this.$set(catalog, 'label', 'Топливные карты');
                this.$set(catalog, 'material_group_ids', []);
                this.$set(catalog, 'excluded_material_group_ids', []);
                this.$set(catalog, 'allow_custom_items', false);
                this.$set(catalog, 'scope', 'all');
                this.$set(this.form.workflow_config, 'buildingProcess', {
                    enabled: false,
                    required: false
                });
                this.$set(this.form.workflow_config, 'requiresBuildingProcess', false);
            } else if (value === 'mechanics') {
                this.$set(catalog, 'enabled', true);
                this.$set(catalog, 'type', 'mechanic_assets');
                this.$set(catalog, 'label', 'Техника');
                this.$set(catalog, 'material_group_ids', []);
                this.$set(catalog, 'excluded_material_group_ids', []);
                this.$set(catalog, 'allow_custom_items', false);
                this.$set(catalog, 'scope', 'all');
                this.$set(this.form.workflow_config, 'mechanicAssetAction', this.getDefaultMechanicAssetActionConfig());
                this.$set(this.form.workflow_config, 'mechanicAssetActions', []);
                this.$set(this.form.workflow_config, 'buildingProcess', {
                    enabled: false,
                    required: false
                });
                this.$set(this.form.workflow_config, 'requiresBuildingProcess', false);
            }
        },
        onItemCatalogTypeChange(value) {
            this.ensureItemCatalogConfig();
            const labels = {
                materials: 'Материалы',
                employees: 'Сотрудники',
                fuel_cards: 'Топливные карты',
                mechanic_assets: 'Техника'
            };

            this.$set(this.form.workflow_config.itemCatalog, 'label', labels[value] || 'Позиции');
            if (value !== 'materials') {
                this.$set(this.form.workflow_config.itemCatalog, 'material_group_ids', []);
                this.$set(this.form.workflow_config.itemCatalog, 'excluded_material_group_ids', []);
            }
            if (value !== 'fuel_cards') {
                this.$set(this.form.workflow_config, 'fuelCardAction', this.getDefaultFuelCardActionConfig());
            }
            if (value !== 'mechanic_assets') {
                this.$set(this.form.workflow_config, 'mechanicAssetAction', this.getDefaultMechanicAssetActionConfig());
                this.$set(this.form.workflow_config, 'mechanicAssetActions', []);
                this.$delete(this.form.workflow_config.itemCatalog, 'scope');
            } else {
                if (!this.form.workflow_config.itemCatalog.scope) {
                    this.$set(this.form.workflow_config.itemCatalog, 'scope', 'all');
                }
                this.ensureMechanicAssetActions();
            }
            if (value !== 'employees') {
                this.$set(this.form.workflow_config.itemCatalog, 'allow_custom_items', false);
            }
        },
        onMechanicAssetCatalogScopeChange(value) {
            this.ensureItemCatalogConfig();
            const scope = this.normalizeMechanicAssetCatalogScope(value);
            this.$set(this.form.workflow_config.itemCatalog, 'scope', scope);

            if (scope === 'mechanic_assets_on_object') {
                this.$set(this.form.workflow_config, 'buildingProcess', {
                    enabled: true,
                    required: true
                });
                this.$set(this.form.workflow_config, 'requiresBuildingProcess', true);
            }
        },
        normalizeInitiatorConfig(config) {
            if (!config) {
                return getDefaultInitiatorConfig();
            }

            const normalized = cloneJson(config, getDefaultInitiatorConfig());
            if (Array.isArray(normalized.participants)) {
                return {
                    participants: normalized.participants,
                    allow_any_department_employee: normalized.allow_any_department_employee === true
                };
            }

            if (normalized.type) {
                return {
                    participants: [normalized],
                    allow_any_department_employee: false
                };
            }

            return getDefaultInitiatorConfig();
        },
        async loadRequestType(id) {
            try {
                const requestTypes = this.requestTypesForLinks.length
                    ? this.requestTypesForLinks
                    : await this.$store.dispatch('requests_getRequestTypes');
                const requestType = requestTypes.find(rt => rt.id === parseInt(id));
                
                if (!requestType) {
                    this.$bvToast.toast('Тип заявки не найден', { 
                        title: 'Ошибка', 
                        variant: 'danger', 
                        solid: true 
                    });
                    this.goBackToList({ requestTypeId: null, replace: true });
                    return;
                }

                this.form = {
                    name: requestType.name,
                    category: requestType.category,
                    description: requestType.description || '',
                    is_active: requestType.is_active,
                    initiator_config: this.normalizeInitiatorConfig(requestType.initiator_config),
                    approvers: requestType.approvers ? JSON.parse(JSON.stringify(requestType.approvers)) : { participants: [], allow_any_department_employee: false },
                    observers: requestType.observers ? JSON.parse(JSON.stringify(requestType.observers)) : { participants: [], allow_any_department_employee: false },
                    executors: requestType.executors ? JSON.parse(JSON.stringify(requestType.executors)) : { participants: [], allow_any_department_employee: false },
                    executor_assignment_manual: requestType.executor_assignment_manual || false,
                    executor_assigner: requestType.executor_assigner ? JSON.parse(JSON.stringify(requestType.executor_assigner)) : { type: 'all' },
                    custom_fields: this.normalizeCustomFieldsForEditor(
                        requestType.custom_fields ? JSON.parse(JSON.stringify(requestType.custom_fields)) : []
                    ),
                    allow_attachments: requestType.allow_attachments !== undefined ? requestType.allow_attachments : true,
                    attachments_required: requestType.attachments_required || false,
                    max_file_size: requestType.max_file_size || 10,
                    max_files_count: requestType.max_files_count || 5,
                    allowed_file_types: requestType.allowed_file_types ? [...requestType.allowed_file_types] : [],
                    allow_comments: requestType.allow_comments !== undefined ? requestType.allow_comments : true,
                    comment_permissions: requestType.comment_permissions || 'all_participants',
                    allow_comment_attachments: requestType.allow_comment_attachments !== undefined ? requestType.allow_comment_attachments : true,
                    max_comment_file_size: requestType.max_comment_file_size || 5,
                    max_comment_files_count: requestType.max_comment_files_count || 3,
                    submission_deadline_days: requestType.submission_deadline_days || 4,
                    is_emergency: requestType.is_emergency || false,
                    requires_justification_if_late: requestType.requires_justification_if_late !== undefined ? requestType.requires_justification_if_late : true,
                    requires_title: requestType.requires_title !== undefined ? requestType.requires_title : true,
                    status_config: requestType.status_config ? JSON.parse(JSON.stringify(requestType.status_config)) : {},
                    workflow_config: requestType.workflow_config ? JSON.parse(JSON.stringify(requestType.workflow_config)) : {},
                    late_approval_config: requestType.late_approval_config 
                        ? JSON.parse(JSON.stringify(requestType.late_approval_config))
                        : { enabled: false, approvers: { participants: [], allow_any_department_employee: false } }
                };
                this.ensureItemCatalogConfig();
            } catch (e) {
                console.error('Ошибка загрузки типа заявки:', e);
                this.$bvToast.toast('Не удалось загрузить тип заявки', { 
                    title: 'Ошибка', 
                    variant: 'danger', 
                    solid: true 
                });
                this.goBackToList({ requestTypeId: null, replace: true });
            }
        },
        onExecutorAssignmentModeChange() {
            if (this.form.executor_assignment_manual && !this.form.executor_assigner) {
                this.form.executor_assigner = { type: 'all' };
            }
        },
        addCustomField() {
            if (!this.form.custom_fields) {
                this.form.custom_fields = [];
            }
            this.form.custom_fields.push({
                _uid: Date.now() + Math.random(),
                label: '',
                key: '',
                type: null,
                placeholder: '',
                required: false,
                allow_manual_entries: false,
                visibility: {
                    enabled: false,
                    field_key: '',
                    operator: 'equals',
                    value: ''
                }
            });
        },
        removeCustomField(index) {
            this.form.custom_fields.splice(index, 1);
        },
        normalizeCustomFieldsForEditor(fields) {
            return (Array.isArray(fields) ? fields : []).map((field, index) => ({
                ...(field || {}),
                _uid: field && field._uid ? field._uid : `custom-field-${Date.now()}-${index}-${Math.random()}`
            }));
        },
        getReservedCustomFieldKeys(index) {
            return (Array.isArray(this.form.custom_fields) ? this.form.custom_fields : [])
                .filter((field, fieldIndex) => fieldIndex !== index && field)
                .map(field => field.key)
                .filter(Boolean);
        },
        moveCustomField(index, direction) {
            if (!Array.isArray(this.form.custom_fields)) {
                return;
            }
            const nextIndex = index + direction;
            if (nextIndex < 0 || nextIndex >= this.form.custom_fields.length) {
                return;
            }
            const [field] = this.form.custom_fields.splice(index, 1);
            this.form.custom_fields.splice(nextIndex, 0, field);
        },
        getCurrentRequestTypeId(savedRequestType) {
            if (savedRequestType && savedRequestType.id) {
                return savedRequestType.id;
            }
            if (this.$route.params.id) {
                return this.$route.params.id;
            }
            return null;
        },
        goBackToList(options = {}) {
            const hasExplicitRequestTypeId = Object.prototype.hasOwnProperty.call(options, 'requestTypeId');
            const requestTypeId = hasExplicitRequestTypeId
                ? options.requestTypeId
                : this.getCurrentRequestTypeId(options.requestType);
            const route = {
                name: 'request-types',
                query: requestTypeId ? { highlight: String(requestTypeId) } : {}
            };
            const method = options.replace ? 'replace' : 'push';
            return this.$router[method](route).catch(() => {});
        },
        handleCancel() {
            this.goBackToList({ replace: true });
        },
        async handleSubmit() {
            if (!this.form.name || !this.form.category) {
                this.$bvToast.toast('Заполните обязательные поля', {
                    title: 'Ошибка',
                    variant: 'danger',
                    solid: true
                });
                return;
            }

            if (!this.isSubmissionExtension) this.ensureItemCatalogConfig();
            if (this.mechanicAssetCatalogEnabled) {
                this.syncPrimaryMechanicAssetAction();
            }

            if (!CARD_BORDER_VARIANTS.includes(this.form.workflow_config.cardBorderVariant)) {
                this.$bvToast.toast('Выберите системный цвет линии карточки', {
                    title: 'Ошибка',
                    variant: 'danger',
                    solid: true
                });
                return;
            }

            if (this.itemCatalogEnabled && !this.form.workflow_config.itemCatalog.type) {
                this.$bvToast.toast('Выберите справочник позиций', {
                    title: 'Ошибка',
                    variant: 'danger',
                    solid: true
                });
                return;
            }

            if (this.fuelCardCatalogEnabled &&
                this.form.workflow_config.fuelCardAction &&
                this.form.workflow_config.fuelCardAction.enabled &&
                !this.form.workflow_config.fuelCardAction.operation) {
                this.$bvToast.toast('Выберите действие со справочником топливных карт', {
                    title: 'Ошибка',
                    variant: 'danger',
                    solid: true
                });
                return;
            }

            if (this.mechanicAssetCatalogEnabled &&
                this.form.workflow_config.mechanicAssetAction &&
                this.form.workflow_config.mechanicAssetAction.enabled) {
                const mechanicActions = Array.isArray(this.form.workflow_config.mechanicAssetActions)
                    ? this.form.workflow_config.mechanicAssetActions
                    : [];
                const invalidAction = mechanicActions.find(action => !action.operation || !action.triggerStatus);
                if (mechanicActions.length === 0 || invalidAction) {
                    this.$bvToast.toast('Выберите действие со справочником механиков', {
                        title: 'Ошибка',
                        variant: 'danger',
                        solid: true
                    });
                    return;
                }
            }

            if (!this.isSubmissionExtension && this.form.workflow_config.linkedRequests &&
                this.form.workflow_config.linkedRequests.enabled) {
                const rules = this.form.workflow_config.linkedRequests.rules || [];
                const activeRules = rules.filter(rule => rule.active !== false);
                const invalidRule = activeRules.find(rule =>
                    !rule.label ||
                    !rule.targetRequestTypeId ||
                    !Array.isArray(rule.allowedRoles) ||
                    rule.allowedRoles.length === 0
                );
                if (invalidRule) {
                    this.$bvToast.toast('Заполните правила связанных заявок', {
                        title: 'Ошибка',
                        variant: 'danger',
                        solid: true
                    });
                    return;
                }
            }

            if (!this.isSubmissionExtension && this.form.workflow_config.requiredAttachments &&
                this.form.workflow_config.requiredAttachments.enabled) {
                const rules = this.form.workflow_config.requiredAttachments.rules || [];
                const activeRules = rules.filter(rule => rule.active !== false);
                const invalidRule = activeRules.find(rule =>
                    !rule.key ||
                    !/^[a-zA-Z0-9_:-]+$/.test(rule.key) ||
                    !rule.label ||
                    !Array.isArray(rule.requiredFor) ||
                    rule.requiredFor.length === 0 ||
                    !Number.isInteger(Number(rule.minCount)) ||
                    Number(rule.minCount) <= 0
                );
                if (invalidRule) {
                    this.$bvToast.toast('Заполните правила обязательных документов', {
                        title: 'Ошибка',
                        variant: 'danger',
                        solid: true
                    });
                    return;
                }
            }

            const customFields = !this.isSubmissionExtension && Array.isArray(this.form.custom_fields) ? this.form.custom_fields : [];
            const isDropdownCustomField = type => type === 'select_single' || type === 'select_multiple';
            const isEmptyCustomFieldOptionValue = value =>
                value === undefined ||
                value === null ||
                (typeof value === 'string' && value.trim() === '');
            const normalizeCustomFieldDropdownOptions = field => {
                const options = Array.isArray(field.options) ? field.options : [];
                return options
                    .map(option => {
                        if (option === null || option === undefined || option === '') {
                            return null;
                        }
                        if (typeof option === 'string' || typeof option === 'number' || typeof option === 'boolean') {
                            const value = typeof option === 'string' ? option.trim() : option;
                            if (isEmptyCustomFieldOptionValue(value)) {
                                return null;
                            }
                            return {
                                value,
                                label: String(value)
                            };
                        }
                        if (typeof option !== 'object' || Array.isArray(option)) {
                            return null;
                        }

                        const rawValue = option.value !== undefined
                            ? option.value
                            : (option.id !== undefined ? option.id : option.key);
                        const rawLabel = [option.label, option.text, option.name, rawValue].find(item =>
                            item !== undefined && item !== null && String(item).trim() !== ''
                        );
                        const label = String(rawLabel || '').trim();
                        const valueSource = isEmptyCustomFieldOptionValue(rawValue) ? label : rawValue;
                        const value = typeof valueSource === 'string' ? valueSource.trim() : valueSource;

                        if (isEmptyCustomFieldOptionValue(value) || !label) {
                            return null;
                        }

                        return {
                            value,
                            label
                        };
                    })
                    .filter(Boolean);
            };
            const hasDuplicateCustomFieldOptions = options => {
                const values = options.map(option => String(option.value).trim().toLowerCase());
                return values.some((value, index) => values.indexOf(value) !== index);
            };
            const invalidCustomField = customFields.find(field => {
                const key = String(field.key || '').trim();
                const label = String(field.label || '').trim();
                const type = String(field.type || '').trim();
                const isReference = type === 'reference_single' || type === 'reference_multiple';
                return !label ||
                    !key ||
                    !/^[a-zA-Z][a-zA-Z0-9_]*$/.test(key) ||
                    !type ||
                    (isReference && !field.reference);
            });
            if (invalidCustomField) {
                this.$bvToast.toast('Заполните название, системное имя и тип каждого дополнительного поля', {
                    title: 'Ошибка',
                    variant: 'danger',
                    solid: true
                });
                return;
            }

            const invalidDropdownField = customFields.find(field => {
                const type = String(field.type || '').trim();
                if (!isDropdownCustomField(type)) {
                    return false;
                }

                const rawOptionsCount = Array.isArray(field.options) ? field.options.length : 0;
                const options = normalizeCustomFieldDropdownOptions(field);
                return rawOptionsCount === 0 ||
                    options.length !== rawOptionsCount ||
                    hasDuplicateCustomFieldOptions(options);
            });
            if (invalidDropdownField) {
                const fieldName = invalidDropdownField.label || invalidDropdownField.key || 'выпадающего списка';
                this.$bvToast.toast(`Заполните варианты для поля "${fieldName}". Значения не должны повторяться`, {
                    title: 'Ошибка',
                    variant: 'danger',
                    solid: true
                });
                return;
            }

            const customFieldKeys = customFields.map(field => String(field.key || '').trim());
            const duplicateCustomFieldKey = customFieldKeys.find((key, index) => customFieldKeys.indexOf(key) !== index);
            if (duplicateCustomFieldKey) {
                this.$bvToast.toast(`Системное имя "${duplicateCustomFieldKey}" используется несколько раз`, {
                    title: 'Ошибка',
                    variant: 'danger',
                    solid: true
                });
                return;
            }

            const seenCustomFieldKeys = new Set();
            const invalidVisibilityField = customFields.find(field => {
                const visibility = field.visibility || {};
                const enabled = visibility.enabled === true;
                const sourceKey = String(visibility.field_key || '').trim();
                const operator = String(visibility.operator || '').trim();
                const needsValue = ['equals', 'not_equals', 'in', 'not_in'].includes(operator);
                const invalid = enabled && (
                    !sourceKey ||
                    !seenCustomFieldKeys.has(sourceKey) ||
                    !operator ||
                    (needsValue && String(visibility.value || '').trim() === '')
                );
                seenCustomFieldKeys.add(String(field.key || '').trim());
                return invalid;
            });
            if (invalidVisibilityField) {
                this.$bvToast.toast('Заполните условие показа зависимого дополнительного поля. Зависеть можно только от поля выше по списку.', {
                    title: 'Ошибка',
                    variant: 'danger',
                    solid: true
                });
                return;
            }

            this.saving = true;
            const payload = JSON.parse(JSON.stringify(this.form));
            payload.initiator_config = this.normalizeInitiatorConfig(payload.initiator_config);
            payload.custom_fields = this.isSubmissionExtension ? payload.custom_fields : Array.isArray(payload.custom_fields)
                ? payload.custom_fields.map(field => {
                    const cleanField = { ...field };
                    delete cleanField._uid;
                    cleanField.key = String(cleanField.key || '').trim();
                    cleanField.label = String(cleanField.label || '').trim();
                    cleanField.type = String(cleanField.type || 'text').trim();
                    cleanField.placeholder = String(cleanField.placeholder || '').trim();
                    cleanField.required = cleanField.required === true;
                    cleanField.allow_manual_entries = cleanField.allow_manual_entries === true;
                    if (isDropdownCustomField(cleanField.type)) {
                        cleanField.options = normalizeCustomFieldDropdownOptions(cleanField);
                    } else {
                        delete cleanField.options;
                    }
                    if (cleanField.type !== 'reference_single' && cleanField.type !== 'reference_multiple') {
                        delete cleanField.reference;
                        delete cleanField.allow_quantities;
                        delete cleanField.filter_by_object;
                        delete cleanField.allow_manual_entries;
                    }
                    if (cleanField.type !== 'reference_multiple') {
                        delete cleanField.allow_quantities;
                    }
                    if (cleanField.visibility && cleanField.visibility.enabled === true) {
                        cleanField.visibility = {
                            enabled: true,
                            field_key: String(cleanField.visibility.field_key || '').trim(),
                            operator: String(cleanField.visibility.operator || 'equals').trim() || 'equals',
                            value: cleanField.visibility.value
                        };
                        if (cleanField.visibility.operator === 'filled' || cleanField.visibility.operator === 'empty') {
                            delete cleanField.visibility.value;
                        } else {
                            cleanField.visibility.value = String(cleanField.visibility.value || '').trim();
                        }
                    } else {
                        delete cleanField.visibility;
                    }
                    return cleanField;
                })
                : [];
            if (!this.isSubmissionExtension && payload.workflow_config &&
                payload.workflow_config.linkedRequests &&
                Array.isArray(payload.workflow_config.linkedRequests.rules)) {
                payload.workflow_config.linkedRequests.rules = payload.workflow_config.linkedRequests.rules.map(rule => {
                    const cleanRule = { ...rule };
                    delete cleanRule._uid;
                    cleanRule.copyDescription = false;
                    cleanRule.copyFiles = false;
                    cleanRule.copyItems = false;
                    cleanRule.transferTargetTeamRolesToSourceObservers =
                        this.normalizeLinkedRequestTransferRoles(cleanRule.transferTargetTeamRolesToSourceObservers);
                    cleanRule.transferSourceTeamRolesToTargetObservers =
                        this.normalizeLinkedRequestTransferRoles(cleanRule.transferSourceTeamRolesToTargetObservers);
                    delete cleanRule.addTargetApproversToSourceObservers;
                    delete cleanRule.add_target_approvers_to_source_observers;
                    delete cleanRule.syncApproversToSourceObservers;
                    delete cleanRule.sync_approvers_to_source_observers;
                    delete cleanRule.addSourceTeamToTargetObservers;
                    delete cleanRule.add_source_team_to_target_observers;
                    delete cleanRule.syncSourceTeamToTargetObservers;
                    delete cleanRule.sync_source_team_to_target_observers;
                    return cleanRule;
                });
            }
            if (!this.isSubmissionExtension && payload.workflow_config &&
                payload.workflow_config.requiredAttachments &&
                Array.isArray(payload.workflow_config.requiredAttachments.rules)) {
                payload.workflow_config.requiredAttachments.rules = payload.workflow_config.requiredAttachments.rules.map(rule => {
                    const cleanRule = { ...rule };
                    delete cleanRule._uid;
                    cleanRule.key = String(cleanRule.key || '').trim().toLowerCase();
                    cleanRule.label = String(cleanRule.label || '').trim();
                    cleanRule.minCount = Number(cleanRule.minCount) || 1;
                    cleanRule.uploadRoles = Array.isArray(cleanRule.uploadRoles)
                        ? cleanRule.uploadRoles.map(role => String(role || '').trim().toLowerCase()).filter(Boolean)
                        : [];
                    cleanRule.activationMode = cleanRule.initiatorChoice ? 'initiator_choice' : 'always';
                    delete cleanRule.initiatorChoice;
                    return cleanRule;
                });
            }
            if (!this.isSubmissionExtension && payload.workflow_config &&
                payload.workflow_config.mechanicAssetAction &&
                Array.isArray(payload.workflow_config.mechanicAssetActions)) {
                const mechanicEnabled = payload.workflow_config.mechanicAssetAction.enabled === true;
                const cleanMechanicAction = action => {
                    const cleanAction = { ...action };
                    delete cleanAction._uid;
                    cleanAction.enabled = mechanicEnabled;
                    return cleanAction;
                };
                payload.workflow_config.mechanicAssetActions =
                    payload.workflow_config.mechanicAssetActions.map(cleanMechanicAction);
                payload.workflow_config.mechanicAssetAction = payload.workflow_config.mechanicAssetActions[0]
                    ? { ...payload.workflow_config.mechanicAssetActions[0] }
                    : cleanMechanicAction(this.getDefaultMechanicAssetActionConfig());
            }

            if (this.isSubmissionExtension) {
                payload.workflow_config = extensionProfile.normalizeWorkflow(payload.workflow_config, payload.allow_comments);
                payload.status_config = extensionProfile.notificationDefaults(payload.status_config);
                payload.attachments_required = false;
            }
            if (this.isReportAnnulment) {
                payload.workflow_config = annulmentProfile.normalizeWorkflow(payload.workflow_config, payload.allow_comments);
                payload.executors = null; payload.executor_assigner = null; payload.executor_assignment_manual = false;
            }
            try {
                let savedRequestType = null;
                if (this.editMode) {
                    savedRequestType = await this.$store.dispatch('requests_updateRequestType', {
                        id: this.$route.params.id,
                        payload
                    });
                    this.$bvToast.toast('Тип заявки обновлен', { 
                        title: 'Успех', 
                        variant: 'success', 
                        solid: true 
                    });
                } else {
                    savedRequestType = await this.$store.dispatch('requests_createRequestType', payload);
                    this.$bvToast.toast('Тип заявки создан', { 
                        title: 'Успех', 
                        variant: 'success', 
                        solid: true 
                    });
                }
                this.goBackToList({ requestType: savedRequestType });
            } catch (e) {
                console.error('Ошибка сохранения типа заявки:', e);
                const responseMessage = e && e.response && e.response.data && e.response.data.message;
                this.$bvToast.toast(responseMessage || 'Ошибка сохранения типа заявки', {
                    title: 'Ошибка', 
                    variant: 'danger', 
                    solid: true 
                });
            } finally {
                this.saving = false;
            }
        },
        async loadRequestTypesForLinks() {
            try {
                const requestTypes = await this.$store.dispatch('requests_getRequestTypes');
                this.requestTypesForLinks = Array.isArray(requestTypes) ? requestTypes : [];
            } catch (error) {
                console.error('Ошибка загрузки типов заявок для связей:', error);
                this.requestTypesForLinks = [];
            }
        },
        async loadUsers() {
            try {
                const data = await this.$store.dispatch('getUsers');
                if (data && Array.isArray(data)) {
                    this.allUsers = data
                        .filter(user => user.active && !user.isBot)
                        .map(user => ({
                            value: user.user_id,
                            text: `${user.first_name} ${user.last_name}`
                        }));
                }
            } catch (error) {
                console.error('Ошибка загрузки пользователей:', error);
                this.$bvToast.toast('Не удалось загрузить список пользователей', { 
                    title: 'Ошибка', 
                    variant: 'warning', 
                    solid: true 
                });
            }
        },
        async loadDepartments() {
            try {
                let departments = this.$store.state.departments || [];
                if (!departments.length) {
                    const loadedDepartments = await this.$store.dispatch('getDepartments');
                    departments = Array.isArray(loadedDepartments)
                        ? loadedDepartments
                        : (this.$store.state.departments || []);
                }

                this.allDepartments = departments
                    .filter(dept => dept && dept.is_active !== false)
                    .map(dept => ({
                        value: dept.id,
                        text: dept.name
                    }))
                    .filter(dept => dept.value && dept.text)
                    .sort((a, b) => a.text.localeCompare(b.text));
            } catch (error) {
                console.error('Ошибка загрузки отделов:', error);
                this.$bvToast.toast('Не удалось загрузить список отделов', { 
                    title: 'Ошибка', 
                    variant: 'warning', 
                    solid: true 
                });
            }
        },
        async loadMaterialGroups() {
            try {
                await this.$store.dispatch('getMaterialGroups');
                const groups = this.$store.state.materials.groups || [];
                this.materialGroupOptions = groups
                    .map(group => ({
                        value: group.id,
                        text: group.name
                    }))
                    .sort((a, b) => a.text.localeCompare(b.text));
            } catch (error) {
                console.error('Ошибка загрузки групп материалов:', error);
                this.materialGroupOptions = [];
            }
        }
    },
    beforeDestroy() {
        this.clearSearchHighlight();
    },
    async mounted() {
        if (typeof window !== 'undefined') {
            window.scrollTo(0, 0);
        }
        this.ensureItemCatalogConfig();
        await Promise.all([
            this.loadRequestTypesForLinks(),
            this.loadUsers(),
            this.loadDepartments(),
            this.loadMaterialGroups()
        ]);
        
        if (this.$route.params.id) {
            this.editMode = true;
            await this.loadRequestType(this.$route.params.id);
        }
    }
}
</script>

<style scoped>
.request-type-editor {
    --request-type-sticky-header-top: 56px;
    --request-type-sidebar-top: 148px;
    color: #253242;
}

.request-type-editor-header {
    position: sticky;
    top: var(--request-type-sticky-header-top);
    z-index: 30;
    display: flex;
    justify-content: space-between;
    gap: 1rem;
    align-items: center;
    margin-bottom: 1rem;
    padding: 0.75rem 0.85rem;
    border: 1px solid #d8dee4;
    border-radius: 6px;
    background: #ffffff;
    box-shadow: 0 2px 8px rgba(18, 38, 63, 0.08);
}

.request-type-editor-header__left {
    display: flex;
    align-items: center;
    min-width: 0;
}

.request-type-editor-title {
    min-width: 0;
}

.request-type-editor-title__mode {
    color: #6c757d;
    font-size: 0.78rem;
    font-weight: 700;
    line-height: 1.1;
    text-transform: uppercase;
}

.request-type-editor-title__name {
    overflow: hidden;
    max-width: 760px;
    margin: 0;
    color: #253242;
    font-size: 1.2rem;
    font-weight: 700;
    line-height: 1.25;
    text-overflow: ellipsis;
    white-space: nowrap;
}

.request-type-editor-title__meta {
    overflow: hidden;
    max-width: 760px;
    color: #6c757d;
    font-size: 0.82rem;
    line-height: 1.2;
    text-overflow: ellipsis;
    white-space: nowrap;
}

.request-type-editor-header__actions {
    display: flex;
    flex-shrink: 0;
    align-items: center;
}

.request-type-shell {
    display: grid;
    grid-template-columns: 280px minmax(0, 1fr);
    gap: 1rem;
    align-items: start;
}

.request-type-sidebar {
    position: sticky;
    top: var(--request-type-sidebar-top);
    z-index: 29;
    display: flex;
    flex-direction: column;
    gap: 0.35rem;
    max-height: calc(100vh - var(--request-type-sidebar-top) - 12px);
    overflow-y: auto;
    padding: 0.75rem;
    border: 1px solid #d8dee4;
    border-radius: 6px;
    background: #ffffff;
}

.request-type-sidebar__caption {
    padding: 0 0.25rem 0.35rem;
    color: #6c757d;
    font-size: 0.78rem;
    font-weight: 700;
    text-transform: uppercase;
}

.request-type-parameter-search {
    min-width: 0;
}

.request-type-parameter-search__meta,
.request-type-parameter-search__empty {
    padding: 0.35rem 0.25rem 0;
    color: #6c757d;
    font-size: 0.78rem;
    line-height: 1.25;
}

.request-type-parameter-search__empty {
    padding: 0.6rem 0.45rem;
    border: 1px dashed #d8dee4;
    border-radius: 6px;
    background: #fbfcfe;
}

.request-type-nav-button {
    display: grid;
    grid-template-columns: 32px minmax(0, 1fr) auto;
    gap: 0.65rem;
    align-items: center;
    width: 100%;
    padding: 0.65rem 0.7rem;
    border: 1px solid transparent;
    border-radius: 6px;
    background: transparent;
    color: #36465a;
    text-align: left;
}

.request-type-nav-button:hover,
.request-type-nav-button:focus {
    border-color: #c9d6e3;
    background: #f7fafc;
    outline: none;
}

.request-type-nav-button--active {
    border-color: #9fc5ee;
    background: #eef6ff;
    color: #0f4c81;
}

.request-type-nav-button__icon {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 32px;
    height: 32px;
    border-radius: 6px;
    background: #eef1f5;
}

.request-type-nav-button--active .request-type-nav-button__icon {
    background: #d8ebff;
}

.request-type-nav-button__body {
    min-width: 0;
}

.request-type-nav-button__title {
    display: block;
    font-weight: 700;
    line-height: 1.2;
}

.request-type-nav-button__summary {
    display: block;
    overflow: hidden;
    color: #6c757d;
    font-size: 0.82rem;
    line-height: 1.25;
    text-overflow: ellipsis;
    white-space: nowrap;
}

.request-type-nav-button__badge {
    align-self: start;
}

.request-type-nav-button__badges {
    display: flex;
    flex-direction: column;
    gap: 0.25rem;
    align-items: flex-end;
    align-self: start;
    min-width: max-content;
}

.request-type-editor >>> .request-type-search-bubble {
    position: absolute;
    top: 0.55rem;
    right: 1rem;
    z-index: 6;
    display: inline-flex;
    align-items: center;
    max-width: 100%;
    border: 1px solid #ffd43b;
    border-radius: 2px;
    background: #ffe45c;
    color: #3b2d00;
    font-size: 0.78rem;
    font-weight: 700;
    line-height: 1.15;
    white-space: nowrap;
    pointer-events: none;
    box-shadow: 0 1px 3px rgba(95, 61, 0, 0.2);
    transition: transform 0.12s ease, opacity 0.12s ease;
}

.request-type-editor >>> .request-type-search-bubble::before,
.request-type-editor >>> .request-type-search-bubble::after {
    position: absolute;
    right: 46px;
    width: 0;
    height: 0;
    content: '';
}

.request-type-editor >>> .request-type-search-bubble::before {
    top: -8px;
    border-right: 8px solid transparent;
    border-bottom: 8px solid #ffd43b;
    border-left: 8px solid transparent;
}

.request-type-editor >>> .request-type-search-bubble::after {
    top: -6px;
    border-right: 6px solid transparent;
    border-bottom: 6px solid #ffe45c;
    border-left: 6px solid transparent;
}

.request-type-editor >>> .request-type-search-bubble--active {
    background: #ffd43b;
}

.request-type-editor >>> .request-type-search-bubble--active::after {
    border-bottom-color: #ffd43b;
}

.request-type-editor >>> .request-type-search-bubble--avoid {
    opacity: 0.92;
    transform: translateY(calc(-100% - 0.5rem));
}

.request-type-editor >>> .request-type-search-bubble__innards {
    padding: 0.32rem 0.55rem;
}

.request-type-editor [data-search-target].request-type-search-target--has-bubble {
    padding-top: 2.6rem !important;
}

.request-type-search-results {
    display: grid;
    gap: 0.25rem;
    margin: -0.15rem 0 0.35rem 2.65rem;
}

.request-type-search-result {
    display: inline-grid;
    grid-template-columns: 14px minmax(0, 1fr);
    gap: 0.35rem;
    align-items: center;
    max-width: 100%;
    padding: 0.3rem 0.45rem;
    border: 1px solid transparent;
    border-radius: 5px;
    background: transparent;
    color: #526273;
    font-size: 0.8rem;
    line-height: 1.25;
    text-align: left;
}

.request-type-search-result:hover,
.request-type-search-result:focus,
.request-type-search-result--active {
    border-color: #c9d6e3;
    background: #f7fafc;
    color: #0f4c81;
    outline: none;
}

.request-type-search-result span {
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
}

.request-type-editor [data-search-target] {
    position: relative;
    scroll-margin-top: 130px;
}

.request-type-editor >>> .request-type-search-word-highlight {
    padding: 0 0.18em;
    border: 1px solid #f08c00;
    border-radius: 4px;
    background: #fff3bf;
    color: #3b2d00;
    font-weight: 700;
    box-shadow: 0 0 0 2px rgba(240, 140, 0, 0.18), 0 0 8px rgba(255, 212, 59, 0.45);
    animation: request-type-search-word-highlight 0.9s ease-in-out 0s 2;
}

@keyframes request-type-search-word-highlight {
    0% {
        background: #fff3bf;
        color: #3b2d00;
        box-shadow: 0 0 0 2px rgba(240, 140, 0, 0.18), 0 0 8px rgba(255, 212, 59, 0.45);
    }
    45% {
        background: #ffe066;
        color: #2b2100;
        box-shadow: 0 0 0 3px rgba(240, 140, 0, 0.28), 0 0 12px rgba(255, 212, 59, 0.65);
    }
    100% {
        background: #fff3bf;
        color: #3b2d00;
        box-shadow: 0 0 0 2px rgba(240, 140, 0, 0.18), 0 0 8px rgba(255, 212, 59, 0.45);
    }
}

.request-type-main {
    min-width: 0;
    padding: 1rem;
    border: 1px solid #d8dee4;
    border-radius: 6px;
    background: #ffffff;
}

.request-type-main__header {
    display: flex;
    justify-content: space-between;
    gap: 1rem;
    align-items: flex-start;
    margin-bottom: 0.85rem;
}

.request-type-summary-grid {
    display: grid;
    grid-template-columns: repeat(4, minmax(0, 1fr));
    gap: 0.75rem;
    margin-bottom: 1rem;
}

.request-type-summary-item {
    min-width: 0;
    padding: 0.75rem;
    border: 1px solid #e0e6ed;
    border-radius: 6px;
    background: #fbfcfe;
}

.request-type-summary-item__label {
    margin-bottom: 0.35rem;
    color: #6c757d;
    font-size: 0.78rem;
    font-weight: 700;
    text-transform: uppercase;
}

.request-type-summary-item__value {
    overflow: hidden;
    font-size: 0.95rem;
    font-weight: 700;
    text-overflow: ellipsis;
    white-space: nowrap;
}

.request-type-issues {
    border-radius: 6px;
}

.request-type-issues__title {
    margin-bottom: 0.35rem;
    font-weight: 700;
}

.request-type-issues__item + .request-type-issues__item {
    margin-top: 0.2rem;
}

.request-type-tabs-content {
    padding-top: 0.25rem;
}

.participant-config-panel {
    position: relative;
    margin-bottom: 1rem;
    padding: 1rem;
    border: 1px solid #d8dee4;
    border-left-width: 5px;
    border-radius: 6px;
    background: #ffffff;
    box-shadow: 0 1px 3px rgba(18, 38, 63, 0.04);
}

.participant-config-panel + .participant-config-panel {
    margin-top: 1.15rem;
}

.participant-config-panel--initiator {
    border-left-color: #2f80ed;
}

.participant-config-panel--approvers {
    border-left-color: #f2a900;
}

.participant-config-panel--observers {
    border-left-color: #17a2b8;
}

.participant-config-panel--executors {
    border-left-color: #28a745;
}

.participant-config-panel__title {
    margin: -1rem -1rem 1rem;
    padding: 0.75rem 1rem;
    border-bottom: 1px solid #e7ecf2;
    border-radius: 0 6px 0 0;
    background: #f7fafc;
    color: #253242;
    font-weight: 700;
}

.participant-config-subpanel {
    margin-bottom: 1rem;
    padding: 0.85rem;
    border: 1px solid #e0e6ed;
    border-radius: 6px;
    background: #f9fbfd;
}

/* Компактные отступы для вкладок */
.request-type-editor >>> .nav-pills .nav-link {
    padding: 0.5rem 1rem;
    font-size: 0.9rem;
}

.request-type-editor >>> .nav-pills .nav-link i {
    margin-right: 0.25rem;
}

/* Улучшение визуальной иерархии */
.request-type-editor >>> .tab-content {
    min-height: 300px;
}

/* Компактные секции */
.border.rounded {
    border-width: 1px !important;
}

/* Поля должны занимать доступную ширину секции, иначе форма превращается в рваную сетку. */
.request-type-editor >>> .form-group {
    width: 100%;
}

.request-type-editor >>> .form-group input[type="text"],
.request-type-editor >>> .form-group input[type="number"],
.request-type-editor >>> .form-group textarea,
.request-type-editor >>> .form-group .v-select {
    width: 100%;
}

.request-type-color-swatch {
    width: 28px;
    height: 28px;
    border: 2px solid #ffffff;
    border-radius: 4px;
    box-shadow: 0 0 0 1px #ced4da;
    cursor: pointer;
}

.request-type-color-swatch--active {
    box-shadow: 0 0 0 2px #212529;
}

.request-type-icon-grid {
    display: grid;
    grid-template-columns: repeat(auto-fill, 40px);
    gap: 0.5rem;
}

.request-type-icon-button {
    width: 40px;
    height: 40px;
    padding: 0;
    border: 1px solid #ced4da;
    border-radius: 4px;
    background: #ffffff;
    color: #495057;
    cursor: pointer;
}

.request-type-icon-button:hover,
.request-type-icon-button:focus {
    border-color: #80bdff;
    color: #007bff;
}

.request-type-icon-button--active {
    border-color: #007bff;
    background: #e7f1ff;
    color: #007bff;
    box-shadow: 0 0 0 2px rgba(0, 123, 255, 0.25);
}

.linked-request-rule >>> .form-group,
.linked-request-rule >>> .form-group input[type="text"],
.linked-request-rule >>> .form-group .v-select,
.required-attachment-rule >>> .form-group,
.required-attachment-rule >>> .form-group input[type="text"],
.required-attachment-rule >>> .form-group input[type="number"],
.required-attachment-rule >>> .form-group .v-select {
    width: 100%;
}

.mechanic-action-rule {
    padding: 0.75rem;
    margin-bottom: 0.75rem;
    border: 1px solid #d8dee4;
    border-radius: 0.25rem;
    background: #fff;
}

.custom-fields-editor {
    max-width: 1120px;
}

.custom-fields-editor__header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    gap: 1rem;
    margin-bottom: 0.75rem;
}

.custom-fields-editor__counter {
    color: #6c757d;
    font-size: 0.85rem;
}

.custom-fields-editor__list {
    display: flex;
    flex-direction: column;
    gap: 0.65rem;
}

.custom-fields-editor__empty {
    padding: 1rem;
    border: 1px dashed #ced4da;
    border-radius: 6px;
    color: #6c757d;
    text-align: center;
    background: #fbfcfd;
}

.custom-fields-editor >>> .form-group,
.custom-fields-editor >>> .form-group input[type="text"],
.custom-fields-editor >>> .form-group input[type="number"],
.custom-fields-editor >>> .form-group textarea,
.custom-fields-editor >>> .form-group .v-select {
    width: 100%;
}

/* Стили для vue-select совместимость с Bootstrap Vue */
.request-type-editor >>> .v-select {
    min-height: 38px;
}

.request-type-editor >>> .v-select .vs__dropdown-toggle {
    border: 1px solid #ced4da;
    border-radius: 0.25rem;
    min-height: 38px;
}

.request-type-editor >>> .v-select .vs__dropdown-toggle:focus-within {
    border-color: #80bdff;
    box-shadow: 0 0 0 0.2rem rgba(0, 123, 255, 0.25);
}

.request-type-editor >>> .v-select .vs__search {
    margin: 0;
    padding: 0.375rem 0.75rem;
    font-size: 1rem;
    line-height: 1.5;
}

.request-type-editor >>> .v-select .vs__selected-options {
    padding: 0.375rem 0.75rem;
}

@media (max-width: 576px) {
    .request-type-editor {
        --request-type-sticky-header-top: 0px;
        --request-type-sidebar-top: 138px;
        margin-left: -0.5rem;
        margin-right: -0.5rem;
    }

    .request-type-editor-header {
        top: var(--request-type-sticky-header-top);
        margin: 0 0 0.65rem;
        padding: 0.55rem 0.65rem;
        align-items: stretch;
        flex-direction: column;
        gap: 0.55rem;
        border-left: 0;
        border-right: 0;
        border-radius: 0;
    }

    .request-type-editor-header__left {
        align-items: flex-start;
    }

    .request-type-editor-title__name,
    .request-type-editor-title__meta {
        max-width: calc(100vw - 7rem);
    }

    .request-type-editor-title__name {
        font-size: 1rem;
        line-height: 1.2;
    }

    .request-type-editor-title__mode {
        font-size: 0.68rem;
    }

    .request-type-editor-header__actions {
        gap: 0.5rem;
        justify-content: flex-end;
    }

    .request-type-editor-header__actions .btn {
        flex: 1 1 0;
        margin-right: 0 !important;
    }

    .request-type-main {
        border-left: 0;
        border-right: 0;
        border-radius: 0;
        padding: 0.75rem;
    }

    .request-type-main__header {
        gap: 0.35rem;
        flex-direction: row;
        margin-bottom: 0.6rem;
    }

    .request-type-main__header h5 {
        font-size: 1rem;
    }

    .request-type-main__header .small {
        font-size: 0.78rem;
    }

    .request-type-summary-grid {
        grid-template-columns: 1fr;
    }

    .request-type-summary-item {
        padding: 0.55rem 0.65rem;
    }

    .request-type-summary-item__label {
        margin-bottom: 0.2rem;
        font-size: 0.68rem;
    }

    .request-type-summary-item__value {
        font-size: 0.85rem;
    }

    .request-type-issues {
        margin-bottom: 0.75rem;
        font-size: 0.85rem;
    }

    .participant-config-panel {
        margin-left: -0.1rem;
        margin-right: -0.1rem;
        padding: 0.75rem;
        border-radius: 5px;
    }

    .participant-config-panel__title {
        margin: -0.75rem -0.75rem 0.75rem;
        padding: 0.65rem 0.75rem;
        font-size: 0.95rem;
    }

    .participant-config-subpanel {
        padding: 0.7rem;
    }

    .border.rounded {
        padding: 0.75rem !important;
    }

    .custom-fields-editor__header {
        align-items: stretch;
        flex-direction: column;
    }
    
    .request-type-editor >>> .form-group {
        margin-bottom: 1rem;
    }

    .request-type-editor >>> .btn {
        min-height: 34px;
    }
}

@media (max-width: 768px) {
    .request-type-shell {
        grid-template-columns: 1fr;
        gap: 0.65rem;
    }

    .request-type-sidebar {
        position: sticky;
        top: var(--request-type-sidebar-top);
        z-index: 29;
        display: flex;
        flex-direction: row;
        gap: 0.45rem;
        max-height: none;
        overflow-x: auto;
        overflow-y: hidden;
        margin: 0 -0.75rem;
        padding: 0.45rem 0.75rem;
        border-left: 0;
        border-right: 0;
        border-radius: 0;
        box-shadow: 0 2px 8px rgba(18, 38, 63, 0.06);
        -webkit-overflow-scrolling: touch;
        scrollbar-width: none;
    }

    .request-type-sidebar::-webkit-scrollbar {
        display: none;
    }

    .request-type-sidebar__caption {
        display: none;
    }

    .request-type-parameter-search {
        flex: 0 0 220px;
        width: 220px;
    }

    .request-type-parameter-search__meta,
    .request-type-parameter-search__empty {
        display: none;
    }

    .request-type-search-results {
        display: inline-flex;
        flex: 0 0 auto;
        gap: 0.35rem;
        margin: 0;
    }

    .request-type-search-result {
        display: inline-flex;
        flex: 0 0 auto;
        max-width: 180px;
        padding: 0.45rem 0.55rem;
        border-color: #d8dee4;
        background: #ffffff;
        white-space: nowrap;
    }

    .request-type-search-result span {
        max-width: 135px;
    }

    .request-type-editor [data-search-target] {
        scroll-margin-top: 155px;
    }

    .request-type-nav-button {
        display: inline-flex;
        flex: 0 0 auto;
        gap: 0.4rem;
        width: auto;
        max-width: 160px;
        padding: 0.45rem 0.55rem;
        border-color: #d8dee4;
        background: #ffffff;
        white-space: nowrap;
    }

    .request-type-nav-button__icon {
        width: 26px;
        height: 26px;
    }

    .request-type-nav-button__summary {
        display: none;
    }

    .request-type-nav-button__title {
        overflow: hidden;
        max-width: 94px;
        font-size: 0.86rem;
        text-overflow: ellipsis;
        white-space: nowrap;
    }

    .request-type-nav-button__badges {
        flex-direction: row;
        gap: 0.2rem;
        align-items: center;
        margin-left: -0.2rem;
    }

    .request-type-summary-grid {
        display: flex;
        gap: 0.55rem;
        overflow-x: auto;
        margin-left: -0.25rem;
        margin-right: -0.25rem;
        padding: 0 0.25rem 0.2rem;
        -webkit-overflow-scrolling: touch;
        scrollbar-width: none;
    }

    .request-type-summary-grid::-webkit-scrollbar {
        display: none;
    }

    .request-type-summary-item {
        flex: 0 0 190px;
    }

    .request-type-editor >>> .nav-pills {
        flex-direction: column;
    }
    
    .request-type-editor >>> .nav-pills .nav-link {
        margin-bottom: 0.25rem;
        text-align: center;
    }
    
    .request-type-editor >>> .tab-content {
        padding: 0.5rem !important;
    }
}

@media (min-width: 769px) and (max-width: 1180px) {
    .request-type-shell {
        grid-template-columns: 240px minmax(0, 1fr);
    }

    .request-type-summary-grid {
        grid-template-columns: repeat(2, minmax(0, 1fr));
    }
}

@media (max-width: 576px) {
    .request-type-summary-item {
        flex-basis: 165px;
    }
}
</style>
